/* ==========================================================================
   مكتبة — theme.js
   ملف واحد فيه أدوات مشتركة + Custom Elements صغيرة.
   كل عنصر يُعرَّف مرة وحدة، ويشتغل فقط إذا موجود في الصفحة (Progressive enhancement:
   المحتوى والروابط والنماذج تشتغل بدون JS).
   ممنوع: jQuery، أو مكتبات خارجية، أو alert().
   ========================================================================== */
(() => {
  'use strict';

  const theme = (window.theme = window.theme || {});
  const root = document.documentElement;

  /* ---------- أدوات مشتركة ---------- */
  const utils = {
    debounce(fn, wait = 250) {
      let t;
      return (...args) => {
        clearTimeout(t);
        t = setTimeout(() => fn.apply(null, args), wait);
      };
    },

    /** تخزين آمن: يرجع null بدل ما يكسر الصفحة في التصفح الخاص أو عند المنع */
    storage: {
      get(key, area = 'local') {
        try {
          const raw = window[`${area}Storage`].getItem(key);
          return raw == null ? null : JSON.parse(raw);
        } catch (e) {
          return null;
        }
      },
      set(key, value, area = 'local') {
        try {
          window[`${area}Storage`].setItem(key, JSON.stringify(value));
          return true;
        } catch (e) {
          return false;
        }
      },
      remove(key, area = 'local') {
        try {
          window[`${area}Storage`].removeItem(key);
        } catch (e) { /* تجاهل */ }
      }
    },

    /** طلب Section Rendering API — يرجع HTML القسم */
    async fetchSection(sectionId, url = window.location.pathname, signal) {
      const u = new URL(url, window.location.origin);
      u.searchParams.set('section_id', sectionId);
      const res = await fetch(u.toString(), { signal, headers: { Accept: 'text/html' } });
      if (!res.ok) throw new Error(String(res.status));
      return res.text();
    },

    /** إرجاع JSON مع رسالة خطأ واضحة */
    async fetchJSON(url, options = {}) {
      const res = await fetch(url, {
        ...options,
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...(options.headers || {}) }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const err = new Error(data.description || data.message || String(res.status));
        err.status = res.status;
        throw err;
      }
      return data;
    },

    /** العناصر القابلة للتركيز داخل حاوية */
    focusable(container) {
      return Array.from(
        container.querySelectorAll(
          'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), summary'
        )
      ).filter((el) => el.offsetParent !== null || el === document.activeElement);
    },

    /** حصر التركيز داخل حاوية (للدروج والنوافذ غير المعتمدة على <dialog>) */
    trapFocus(container, event) {
      if (event.key !== 'Tab') return;
      const els = utils.focusable(container);
      if (!els.length) return;
      const first = els[0];
      const last = els[els.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },

    /** حالة التحميل للأزرار: aria-busy + نص «جارٍ…» + منع النقر المتكرر */
    setBusy(button, busy, label) {
      if (!button) return;
      if (busy) {
        if (!button.dataset.label) button.dataset.label = button.innerHTML;
        button.setAttribute('aria-busy', 'true');
        button.setAttribute('aria-disabled', 'true');
        if (label) button.textContent = label;
      } else {
        button.removeAttribute('aria-busy');
        button.removeAttribute('aria-disabled');
        if (button.dataset.label) {
          button.innerHTML = button.dataset.label;
          delete button.dataset.label;
        }
      }
    },

    /** تحويل الأرقام حسب إعداد «شكل الأرقام» */
    num(value) {
      const s = String(value);
      if (theme.settings && theme.settings.numerals === 'arabic') {
        return s.replace(/[0-9]/g, (d) => '٠١٢٣٤٥٦٧٨٩'[d]);
      }
      return s;
    },

    reducedMotion() {
      return root.classList.contains('motion-none') || window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }
  };
  theme.utils = utils;

  /* ---------- الارتفاعات المحسوبة (03 §8) ----------
     أي عنصر عليه data-measure="header" (أو announcement / bottom-nav / buy-bar)
     يُقاس ارتفاعه ويُكتب في متغير CSS، حتى ما يغطي عنصر عنصرًا. */
  const measureMap = {
    announcement: '--announcement-h',
    header: '--header-h',
    'header-sticky': '--header-sticky-h',
    'bottom-nav': '--bottom-nav-h',
    'buy-bar': '--buy-bar-h'
  };
  const measured = new Map();
  const ro = 'ResizeObserver' in window
    ? new ResizeObserver((entries) => {
        entries.forEach((entry) => {
          const key = measured.get(entry.target);
          if (!key) return;
          const visible = entry.target.offsetParent !== null || getComputedStyle(entry.target).position === 'fixed';
          const h = visible ? Math.round(entry.target.getBoundingClientRect().height) : 0;
          root.style.setProperty(measureMap[key], `${h}px`);
        });
      })
    : null;

  function observeMeasures(scope = document) {
    if (!ro) return;
    scope.querySelectorAll('[data-measure]').forEach((el) => {
      const key = el.dataset.measure;
      if (!measureMap[key] || measured.has(el)) return;
      measured.set(el, key);
      ro.observe(el);
    });
  }

  /* ---------- تسجيل المكونات ----------
     theme.define('tag-name', class) — يعرّف العنصر مرة وحدة فقط. */
  theme.define = (name, ctor) => {
    if (!customElements.get(name)) customElements.define(name, ctor);
  };

  const S = () => theme.strings || {};
  const mqMobile = window.matchMedia('(max-width: 749px)');
  const mqTouchNav = window.matchMedia('(max-width: 989px)');

  const lockScroll = (on) => root.classList.toggle('is-locked', on);
  const escapeHTML = (str) => String(str).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  /** تطبيع استعلام البحث العربي (مرجع الكود): يشيل التشكيل والكشيدة فقط، ولا يغيّر الهمزات */
  utils.normalizeArabic = (q) => String(q).replace(/[ً-ٰـ]/g, '').replace(/\s+/g, ' ').trim();
  /* للبحث المحلي داخل الصفحة فقط (الطرفين): توحيد الهمزات والألف المقصورة والتاء المربوطة. بحث شوبيفاي يستخدم normalizeArabic بدونها (مرجع الكود) */
  utils.foldArabic = (q) => utils.normalizeArabic(q).replace(/[أإآٱ]/g, 'ا').replace(/ى/g, 'ي').replace(/ة/g, 'ه').toLowerCase();

  /** تنسيق السعر بنفس صيغة المتجر */
  utils.formatMoney = (cents) => {
    const format = (theme.settings && theme.settings.moneyFormat) || '${{amount}}';
    const value = Number(cents || 0) / 100;
    const fmt = (n, dec, thou = ',', d = '.') => {
      const [i, f] = n.toFixed(dec).split('.');
      return i.replace(/\B(?=(\d{3})+(?!\d))/g, thou) + (f ? d + f : '');
    };
    const out = format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return fmt(value, 0);
        case 'amount_with_comma_separator': return fmt(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return fmt(value, 0, '.');
        default: return fmt(value, 2);
      }
    });
    return utils.num(out.replace(/<[^>]*>/g, ''));
  };

  /* ==========================================================================
     الرسائل المنبثقة (Toast) — 04 §13
     theme.toast(text, { type: 'success'|'info'|'warning'|'error', title, action: { label, url }, duration })
     ========================================================================== */
  class ToastRegion extends HTMLElement {
    connectedCallback() {
      this.polite = document.createElement('div');
      this.polite.setAttribute('aria-live', 'polite');
      this.assertive = document.createElement('div');
      this.assertive.setAttribute('aria-live', 'assertive');
      this.polite.className = this.assertive.className = 'toast-region__live';
      this.append(this.polite, this.assertive);
    }
    show(text, { type = 'info', title, action, duration } = {}) {
      const icons = { success: 'success', info: 'info', warning: 'alert', error: 'error' };
      const el = document.createElement('div');
      el.className = `toast toast--${type}`;
      const heading = title || (S().toastTitles || {})[type] || '';
      el.innerHTML = `
        <svg class="icon icon--sm" aria-hidden="true" focusable="false"><use href="#icon-${icons[type] || 'info'}"></use></svg>
        <div>
          ${heading ? `<strong class="toast__title">${escapeHTML(heading)}</strong>` : ''}
          ${text ? `<p class="toast__text">${escapeHTML(text)}</p>` : ''}
          ${action && action.url ? `<a class="toast__action" href="${escapeHTML(action.url)}">${escapeHTML(action.label)}</a>` : ''}
        </div>
        <button type="button" class="icon-btn toast__close" aria-label="${escapeHTML(S().close || '')}">
          <svg class="icon icon--sm" aria-hidden="true" focusable="false"><use href="#icon-close"></use></svg>
        </button>`;
      (type === 'error' ? this.assertive : this.polite).appendChild(el);
      const remove = () => {
        el.classList.add('is-leaving');
        setTimeout(() => el.remove(), utils.reducedMotion() ? 0 : 220);
      };
      el.querySelector('.toast__close').addEventListener('click', remove);
      const ms = duration ?? { success: 4000, info: 5000, warning: 6000, error: 0 }[type];
      if (ms) {
        let timer = setTimeout(remove, ms);
        el.addEventListener('mouseenter', () => clearTimeout(timer));
        el.addEventListener('focusin', () => clearTimeout(timer));
        el.addEventListener('mouseleave', () => { timer = setTimeout(remove, 2000); });
      }
      return el;
    }
  }
  theme.define('toast-region', ToastRegion);
  theme.toast = (text, opts) => {
    const region = document.querySelector('toast-region');
    return region && region.show ? region.show(text, opts) : null;
  };

  /* ==========================================================================
     السلة: Cart AJAX API + Section Rendering API (04 §12) — بدون إعادة تحميل
     theme.cart.add / change / update / rerender / afterAdd
     ========================================================================== */
  const SC = () => S().cart || {};
  const jsonPost = (url, body) => utils.fetchJSON(url, { method: 'POST', body: JSON.stringify(body) });
  theme.cart = {
    async add(formData) {
      const res = await fetch(`${theme.routes.cartAdd}.js`, {
        method: 'POST',
        body: formData,
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.status) throw new Error(data.description || data.message || String(res.status));
      return data;
    },
    async refresh() {
      const cart = await utils.fetchJSON(`${theme.routes.cart}.js`);
      theme.cart.updateCount(cart.item_count);
      theme.cart.state = cart;
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
      await theme.cart.rerender();
      return cart;
    },
    /** تغيير كمية صف (بالمفتاح). 0 = حذف */
    async change(key, quantity) {
      const cart = await jsonPost(`${theme.routes.cartChange}.js`, { id: key, quantity });
      theme.cart.state = cart;
      theme.cart.updateCount(cart.item_count);
      await theme.cart.rerender();
      return cart;
    },
    /** تحديث عام: updates / note / discount / attributes */
    async update(body, { render = true } = {}) {
      const cart = await jsonPost(`${theme.routes.cartUpdate}.js`, body);
      theme.cart.state = cart;
      theme.cart.updateCount(cart.item_count);
      if (render) await theme.cart.rerender();
      return cart;
    },
    /** إعادة عرض كل أجزاء السلة الظاهرة في الصفحة (الجانبية + صفحة السلة) بطلب واحد */
    async rerender() {
      const hosts = Array.from(document.querySelectorAll('[data-cart-content]'));
      if (!hosts.length) return;
      const ids = [...new Set(hosts.map((h) => h.dataset.cartContent))];
      const u = new URL(theme.routes.root, window.location.origin);
      u.searchParams.set('sections', ids.join(','));
      const res = await fetch(u.toString(), { headers: { Accept: 'application/json' } });
      if (!res.ok) throw new Error(String(res.status));
      const map = await res.json();
      hosts.forEach((host) => {
        const html = map[host.dataset.cartContent];
        if (!html) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector(`[data-cart-content="${host.dataset.cartContent}"]`);
        if (!fresh) return;
        const focusKey = theme.cart.focusKey(host);
        host.innerHTML = fresh.innerHTML;
        initGates(host);
        initFaqBlocks(host);
        theme.cart.restoreFocus(host, focusKey);
        host.dispatchEvent(new CustomEvent('cart:rendered', { bubbles: true }));
      });
    },
    /** نحفظ مكان التركيز قبل إعادة العرض ونرجعه بعدها (لوحة المفاتيح + قارئ الشاشة) */
    focusKey(host) {
      const a = document.activeElement;
      if (!a || !host.contains(a)) return null;
      if (a.id) return `#${CSS.escape(a.id)}`;
      const line = a.closest('[data-key]');
      if (line) {
        const kind = a.matches('[data-remove]') ? '[data-remove]' : a.matches('[data-qty-step="1"]') ? '[data-qty-step="1"]' : a.matches('[data-qty-step="-1"]') ? '[data-qty-step="-1"]' : '[data-qty-input]';
        return { line: line.dataset.line, kind };
      }
      return '__head';
    },
    restoreFocus(host, key) {
      if (!key) return;
      let el = null;
      if (typeof key === 'string' && key !== '__head') el = host.querySelector(key);
      if (!el && typeof key === 'object') {
        const line = host.querySelector(`[data-line="${key.line}"]`) || host.querySelector(`[data-line="${Number(key.line) - 1}"]`);
        el = line && (line.querySelector(key.kind) || line.querySelector('[data-remove]'));
      }
      if (!el) el = host.querySelector('[data-drawer-close], h1, h2');
      if (el) {
        if (!el.matches('a, button, input, textarea, select, summary')) el.setAttribute('tabindex', '-1');
        el.focus({ preventScroll: true });
      }
    },
    announce(text) {
      document.querySelectorAll('[data-cart-live]').forEach((el) => { el.textContent = ''; setTimeout(() => { el.textContent = text; }, 50); });
    },
    updateCount(count) {
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = utils.num(count);
        el.hidden = count === 0 && !(theme.settings && theme.settings.cartBadgeZero);
      });
      const label = (S().cartCount || '').replace('__COUNT__', utils.num(count));
      document.querySelectorAll('[data-cart-link]').forEach((a) => a.setAttribute('aria-label', label));
    },
    drawer() {
      const d = document.querySelector('cart-drawer');
      return d && typeof d.open === 'function' && d.querySelector('dialog') ? d : null;
    },
    afterAdd(opener) {
      const mode = (theme.settings && theme.settings.afterAdd) || 'drawer';
      if (mode === 'page') {
        window.location.href = theme.routes.cart;
        return;
      }
      const drawer = theme.cart.drawer();
      if (mode === 'drawer' && drawer) {
        drawer.open(opener);
        return;
      }
      theme.toast(S().added, { type: 'success', action: { label: S().viewCart, url: theme.routes.cart } });
    }
  };

  /* ---------- بوابة تأكيد PDF (02 §8) ----------
     الخانة required داخل نموذج السلة (المتصفح يمنع بدونها). فوقها: زر الدفع معطّل بصريًا وفعليًا،
     والتأكيد ينحفظ في sessionStorage مربوطًا ببصمة السلة؛ أي تغيير في الأصناف أو الكميات = يرجع غير محدد. */
  const GATE_KEY = 'maktaba:pdf-confirmed';
  function gateButtons(gate) {
    const check = gate.querySelector('[data-gate-check]');
    const formId = check && check.getAttribute('form');
    return formId ? Array.from(document.querySelectorAll(`[data-checkout][form="${formId}"]`)) : [];
  }
  function syncGate(gate) {
    const check = gate.querySelector('[data-gate-check]');
    if (!check) return;
    const ok = check.checked;
    gateButtons(gate).forEach((btn) => {
      btn.setAttribute('aria-disabled', String(!ok));
      btn.classList.toggle('is-disabled', !ok);
      const bar = btn.closest('[data-pay-bar]');
      if (bar && bar.dataset.payBar === 'hidden') bar.hidden = !ok;
      if (ok) btn.removeAttribute('aria-describedby'); else btn.setAttribute('aria-describedby', `${check.id}-hint`);
    });
    if (ok) {
      const hint = gate.querySelector('[data-gate-hint]');
      if (hint) hint.hidden = true;
    }
  }
  function initGates(scope = document) {
    scope.querySelectorAll('[data-pdf-gate]').forEach((gate) => {
      const check = gate.querySelector('[data-gate-check]');
      if (!check || gate.dataset.gateInit) return;
      gate.dataset.gateInit = '1';
      const fp = gate.dataset.fingerprint || '';
      const saved = utils.storage.get(GATE_KEY, 'session');
      check.checked = !!(saved && saved === fp && fp);
      check.addEventListener('change', () => {
        if (check.checked) utils.storage.set(GATE_KEY, fp, 'session'); else utils.storage.remove(GATE_KEY, 'session');
        syncGate(gate);
      });
      syncGate(gate);
    });
  }
  /* أي محاولة دفع (نقر، أو Enter، أو زر شريط الدفع) تمر من هنا قبل الإرسال */
  function blockCheckout(e, form) {
    const check = form && document.querySelector(`[data-gate-check][form="${form.id}"]`);
    if (!check || check.checked) return false;
    e.preventDefault();
    const gate = check.closest('[data-pdf-gate]');
    const hint = gate && gate.querySelector('[data-gate-hint]');
    if (hint) hint.hidden = false;
    check.focus();
    gate && gate.classList.remove('is-shaking');
    void (gate && gate.offsetWidth);
    gate && gate.classList.add('is-shaking');
    return true;
  }
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-checkout]');
    if (!btn) return;
    blockCheckout(e, btn.form);
  }, true);
  document.addEventListener('submit', (e) => {
    const form = e.target;
    if (!form.matches || !form.matches('[data-cart-form]')) return;
    if (blockCheckout(e, form)) return;
    // ملاحظة الطلب تنحفظ مع الإرسال (name="note" مربوط بالنموذج)
  }, true);

  /* ---------- سلوك السلة المشترك (الجانبية + صفحة السلة): الحذف، والكمية، والترقية، والخصم، والملاحظة ---------- */
  class CartUI extends HTMLElement {
    connectedCallback() {
      initGates(this);
      this.addEventListener('click', (e) => this.onClick(e));
      this.addEventListener('change', (e) => this.onChange(e));
      this.addEventListener('submit', (e) => this.onSubmit(e));
      this.saveNote = utils.debounce((v) => theme.cart.update({ note: v }, { render: false }).catch(() => {}), 600);
      this.addEventListener('input', (e) => { if (e.target.matches('[data-cart-note]')) this.saveNote(e.target.value); });
    }
    busy(on) {
      const host = this.querySelector('[data-cart-content]') || this;
      host.classList.toggle('is-updating', on);
      host.setAttribute('aria-busy', String(on));
    }
    lineError(line, text) {
      const el = line && line.querySelector('[data-line-error]');
      if (el) { el.textContent = text; el.hidden = false; } else theme.toast(text, { type: 'error' });
    }
    async run(task, line) {
      this.busy(true);
      try {
        await task();
      } catch (err) {
        if (err && err.status === 422) this.lineError(line, err.message && /[؀-ۿ]/.test(err.message) ? err.message : SC().invalidQty);
        else theme.toast(SC().updateError, { type: 'error' });
      } finally {
        this.busy(false);
      }
    }
    onClick(e) {
      const remove = e.target.closest('[data-remove]');
      if (remove) {
        e.preventDefault();
        const line = remove.closest('[data-key]');
        this.run(async () => {
          const cart = await theme.cart.change(line.dataset.key, 0);
          theme.cart.announce(SC().removed);
          this.afterChange(cart);
        }, line);
        return;
      }
      const step = e.target.closest('[data-qty-step]');
      if (step) {
        const line = step.closest('[data-key]');
        const input = line.querySelector('[data-qty-input]');
        const next = Math.max(0, Number(input.value || 0) + Number(step.dataset.qtyStep));
        input.value = next;
        this.setQty(line, next);
        return;
      }
      const up = e.target.closest('[data-upgrade]');
      if (up) {
        if (up.getAttribute('aria-busy') === 'true') return;
        utils.setBusy(up, true, S().processing);
        const keys = (up.dataset.removeKeys || '').split(',').filter(Boolean);
        this.run(async () => {
          const updates = Object.fromEntries(keys.map((k) => [k, 0]));
          await theme.cart.update({ updates }, { render: false });
          const fd = new FormData();
          fd.append('id', up.dataset.bundleVariant);
          fd.append('quantity', 1);
          await theme.cart.add(fd);
          const cart = await theme.cart.refresh();
          theme.cart.announce(SC().upgraded);
          theme.toast(SC().upgraded, { type: 'success' });
          this.afterChange(cart);
        }).finally(() => { if (document.contains(up)) utils.setBusy(up, false); });
        return;
      }
      const rm = e.target.closest('[data-discount-remove]');
      if (rm) {
        const form = this.querySelector('[data-discount-form]');
        const codes = (form ? form.dataset.codes : '').split(',').map((c) => c.trim()).filter((c) => c && c !== rm.dataset.discountRemove);
        this.run(async () => {
          await theme.cart.update({ discount: codes.join(',') });
          this.discountMsg(SC().discountRemoved, 'success');
        });
      }
    }
    onChange(e) {
      const input = e.target.closest('[data-qty-input]');
      if (input) {
        const v = Number(input.value);
        const line = input.closest('[data-key]');
        if (!Number.isInteger(v) || v < 0 || v > 99) { this.lineError(line, SC().invalidQty); return; }
        this.setQty(line, v);
      }
    }
    setQty(line, qty) {
      clearTimeout(this.qtyTimer);
      this.qtyTimer = setTimeout(() => {
        this.run(async () => {
          const cart = await theme.cart.change(line.dataset.key, qty);
          theme.cart.announce(qty === 0 ? SC().removed : SC().updated);
          this.afterChange(cart);
        }, line);
      }, 350);
    }
    onSubmit(e) {
      const form = e.target.closest('[data-discount-form]');
      if (!form) return;
      e.preventDefault();
      const input = form.querySelector('[data-discount-input]');
      const code = input.value.trim();
      if (!code) { this.discountMsg(SC().discountEmpty, 'error'); input.focus(); return; }
      const codes = (form.dataset.codes || '').split(',').map((c) => c.trim()).filter(Boolean);
      if (!codes.some((c) => c.toLowerCase() === code.toLowerCase())) codes.push(code);
      this.run(async () => {
        const cart = await theme.cart.update({ discount: codes.join(',') });
        const found = (cart.discount_codes || []).find((d) => String(d.code).toLowerCase() === code.toLowerCase());
        const ok = !!(found && found.applicable);
        if (!ok) await theme.cart.update({ discount: codes.filter((c) => c.toLowerCase() !== code.toLowerCase()).join(',') });
        this.discountMsg(ok ? SC().discountApplied : SC().discountInvalid, ok ? 'success' : 'error', ok ? '' : code);
      });
    }
    discountMsg(text, type, keep) {
      const msg = this.querySelector('[data-discount-msg]');
      if (!msg) return;
      msg.textContent = text;
      msg.className = `cdisc__msg cdisc__msg--${type}`;
      msg.hidden = false;
      const det = msg.closest('details');
      if (det) det.open = true;
      if (keep) { const input = this.querySelector('[data-discount-input]'); if (input) { input.value = keep; input.setAttribute('aria-invalid', 'true'); } }
    }
    afterChange() {}
  }

  class CartDrawer extends CartUI {
    connectedCallback() {
      super.connectedCallback();
      this.dialog = this.querySelector('dialog');
      if (!this.dialog) return;
      this.dialog.addEventListener('click', (e) => {
        if (e.target === this.dialog) this.close();
        if (e.target.closest('[data-drawer-close]')) this.close();
      });
      this.dialog.addEventListener('close', () => {
        lockScroll(false);
        if (this.opener && document.contains(this.opener)) this.opener.focus();
      });
    }
    open(opener) {
      this.opener = opener || document.activeElement;
      if (!this.dialog.open) {
        this.dialog.showModal();
        lockScroll(true);
      }
      const head = this.dialog.querySelector('[data-drawer-close]');
      if (head) head.focus();
    }
    close() { if (this.dialog && this.dialog.open) this.dialog.close(); }
  }
  theme.define('cart-drawer', CartDrawer);

  class CartPage extends CartUI {
    afterChange(cart) {
      // السلة صارت فاضية: نعيد تحميل الصفحة حتى يظهر «شاهدتها مؤخرًا» وحالة الفراغ كاملة
      if (cart && cart.item_count === 0) window.location.reload();
    }
  }
  theme.define('cart-page', CartPage);

  /* أيقونة السلة تفتح الجانبية (إعداد «أيقونة السلة») — بدون JS تبقى رابطًا لصفحة السلة */
  document.addEventListener('click', (e) => {
    const link = e.target.closest('[data-cart-link], a[href$="/cart"].bottom-nav__link');
    if (!link || e.metaKey || e.ctrlKey || e.shiftKey) return;
    if (!theme.settings || theme.settings.cartIcon !== 'drawer' || theme.settings.pageType === 'cart') return;
    const drawer = theme.cart.drawer();
    if (!drawer) return;
    e.preventDefault();
    drawer.open(link);
    theme.cart.rerender().catch(() => {});
  });
  /* الرجوع بزر المتصفح (bfcache): السلة يمكن تغيّرت في صفحة ثانية */
  window.addEventListener('pageshow', (e) => {
    if (!e.persisted) return;
    theme.cart.refresh().catch(() => {});
  });

  /** نموذج إضافة للسلة (الكروت + صفحة المنتج + شريط الشراء) — بدون JS يرسل عادي */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.form.addEventListener('submit', (e) => this.onSubmit(e));
    }
    async onSubmit(e) {
      e.preventDefault();
      const submitter = e.submitter && e.submitter.matches('[type="submit"]') ? e.submitter : this.form.querySelector('[type="submit"]');
      const btn = submitter || this.form.querySelector('[type="submit"]');
      if (!btn || btn.getAttribute('aria-busy') === 'true' || btn.getAttribute('aria-disabled') === 'true') return;
      const err = this.querySelector('[data-form-error]');
      if (err) err.hidden = true;
      utils.setBusy(btn, true, S().adding);
      try {
        await theme.cart.add(new FormData(this.form));
        await theme.cart.refresh();
        theme.cart.afterAdd(btn);
      } catch (error) {
        if (err) { err.textContent = `${S().addError}. ${S().tryAgain}`; err.hidden = false; }
        theme.toast(S().tryAgain, { type: 'error', title: S().addError });
      } finally {
        utils.setBusy(btn, false);
      }
    }
  }
  theme.define('product-form', ProductForm);

  /* ==========================================================================
     نافذة الاختيار السريع للنسخة
     ========================================================================== */
  class QuickPick extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.list = this.querySelector('[data-qp-list]');
      this.titleEl = this.querySelector('[data-qp-title]');
      this.priceEl = this.querySelector('[data-qp-price]');
      this.mediaEl = this.querySelector('[data-qp-media]');
      this.addBtn = this.querySelector('[data-qp-add]');
      this.addLabel = this.addBtn.textContent.trim();
      this.dialog.addEventListener('click', (e) => { if (e.target === this.dialog) this.dialog.close(); });
      this.querySelector('[data-modal-close]').addEventListener('click', () => this.dialog.close());
      this.dialog.addEventListener('close', () => { lockScroll(false); if (this.opener) this.opener.focus(); });
      this.addBtn.addEventListener('click', () => this.add());
      document.addEventListener('click', (e) => {
        const trigger = e.target.closest('[data-quick-pick]');
        if (trigger) { e.preventDefault(); this.open(trigger); }
      });
    }
    async open(trigger) {
      this.opener = trigger;
      utils.setBusy(trigger, true);
      try {
        const product = await utils.fetchJSON(`${trigger.dataset.quickPick}.js`);
        this.product = product;
        this.titleEl.textContent = product.title;
        const img = product.featured_image;
        this.mediaEl.innerHTML = img ? `<img src="${escapeHTML(String(img).replace(/^\/\//, 'https://'))}${String(img).includes('?') ? '&' : '?'}width=240" alt="" loading="lazy">` : '';
        this.list.innerHTML = '';
        const firstAvailable = product.variants.find((v) => v.available) || null;
        product.variants.forEach((v, i) => {
          const id = `QuickPick-${product.id || 'p'}-${i}`;
          const label = document.createElement('label');
          label.className = 'qp-option';
          label.htmlFor = id;
          if (!v.available) label.setAttribute('aria-disabled', 'true');
          label.innerHTML = `
            <input type="radio" name="quick-pick-variant" id="${id}" value="${v.id}" ${v.available ? '' : 'disabled'} ${firstAvailable && v.id === firstAvailable.id ? 'checked' : ''}>
            <span class="qp-option__name">${escapeHTML(v.title)}</span>
            <span class="qp-option__price">${v.available ? `<bdi>${escapeHTML(utils.formatMoney(v.price))}</bdi>` : escapeHTML(S().unavailable)}</span>`;
          label.querySelector('input').addEventListener('change', () => this.select(v));
          this.list.appendChild(label);
        });
        this.select(firstAvailable);
        this.dialog.showModal();
        lockScroll(true);
        const checked = this.list.querySelector('input:checked');
        if (checked) checked.focus();
      } catch (e) {
        window.location.href = trigger.dataset.quickPick;
      } finally {
        utils.setBusy(trigger, false);
      }
    }
    select(variant) {
      this.variant = variant;
      if (!variant) {
        this.priceEl.textContent = '';
        this.addBtn.setAttribute('aria-disabled', 'true');
        this.addBtn.textContent = S().unavailable;
        return;
      }
      this.priceEl.textContent = utils.formatMoney(variant.price);
      this.addBtn.removeAttribute('aria-disabled');
      this.addBtn.innerHTML = `${escapeHTML(this.addLabel)} · <bdi>${escapeHTML(utils.formatMoney(variant.price))}</bdi>`;
    }
    async add() {
      const v = this.variant;
      if (!v || !v.available || this.addBtn.getAttribute('aria-busy') === 'true') return;
      utils.setBusy(this.addBtn, true, S().adding);
      const fd = new FormData();
      fd.append('id', v.id);
      fd.append('quantity', 1);
      try {
        await theme.cart.add(fd);
        await theme.cart.refresh();
        utils.setBusy(this.addBtn, false);
        this.dialog.close();
        theme.cart.afterAdd();
      } catch (e) {
        utils.setBusy(this.addBtn, false);
        theme.toast(S().tryAgain, { type: 'error', title: S().addError });
      }
    }
  }
  theme.define('quick-pick', QuickPick);

  /* ==========================================================================
     نوافذ عامة: [data-dialog-open="id"] يفتح <dialog id> كنافذة (Sheet في الجوال)
     الإغلاق: [data-modal-close]، أو Escape، أو النقر خارجها. التركيز يرجع للزر.
     ========================================================================== */
  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-dialog-open]');
    if (!opener) return;
    const dlg = document.getElementById(opener.dataset.dialogOpen);
    if (!dlg || typeof dlg.showModal !== 'function') return;
    e.preventDefault();
    if (!dlg.dataset.dialogInit) {
      dlg.dataset.dialogInit = '1';
      dlg.addEventListener('click', (ev) => {
        if (ev.target === dlg || ev.target.closest('[data-modal-close]')) dlg.close();
      });
      dlg.addEventListener('close', () => {
        lockScroll(false);
        if (dlg._opener && document.contains(dlg._opener)) dlg._opener.focus();
      });
    }
    dlg._opener = opener;
    dlg.showModal();
    lockScroll(true);
  });

  /* ==========================================================================
     معرض المنتج — سحب + مصغرات + عداد + تكبير (05 §4)
     ========================================================================== */
  const slideOffset = (track, slide) => slide.offsetLeft - track.firstElementChild.offsetLeft;
  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-gallery-track]');
      if (!this.track) return;
      this.slides = Array.from(this.track.children);
      this.thumbs = Array.from(this.querySelectorAll('[data-thumb]'));
      this.current = this.querySelector('[data-gallery-current]');
      this.live = this.querySelector('[data-gallery-live]');
      this.index = 0;
      this.track.addEventListener('scroll', utils.debounce(() => this.sync(), 60), { passive: true });
      this.thumbs.forEach((t) => t.addEventListener('click', (e) => { e.preventDefault(); this.go(Number(t.dataset.thumb)); }));
      const prev = this.querySelector('[data-gallery-prev]');
      const next = this.querySelector('[data-gallery-next]');
      if (prev) prev.addEventListener('click', () => this.go(this.index - 1));
      if (next) next.addEventListener('click', () => this.go(this.index + 1));
      this.track.addEventListener('keydown', (e) => {
        const rtl = getComputedStyle(this).direction === 'rtl';
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.go(this.index + (rtl ? 1 : -1)); }
        if (e.key === 'ArrowRight') { e.preventDefault(); this.go(this.index + (rtl ? -1 : 1)); }
      });
      this.addEventListener('click', (e) => {
        const z = e.target.closest('[data-zoom]');
        if (!z) return;
        const viewer = this.querySelector('media-viewer');
        if (viewer && viewer.open) viewer.open(Number(z.dataset.zoom), z);
      });
      this.update(0);
    }
    go(i, smooth = true) {
      if (!this.slides.length) return;
      const n = (i + this.slides.length) % this.slides.length;
      this.track.scrollTo({ left: slideOffset(this.track, this.slides[n]), behavior: smooth && !utils.reducedMotion() ? 'smooth' : 'auto' });
      this.update(n);
    }
    goToMedia(id) {
      const i = this.slides.findIndex((s) => s.dataset.mediaId === String(id));
      if (i >= 0) this.go(i);
    }
    sync() {
      const w = this.track.clientWidth || 1;
      const i = Math.round(Math.abs(this.track.scrollLeft) / w);
      if (i !== this.index) this.update(Math.min(i, this.slides.length - 1));
    }
    update(i) {
      this.index = i;
      this.slides.forEach((s, k) => {
        const on = k === i;
        s.toggleAttribute('aria-hidden', !on);
        const z = s.querySelector('[data-zoom]');
        if (z) z.tabIndex = on ? 0 : -1;
      });
      this.thumbs.forEach((t, k) => (k === i ? t.setAttribute('aria-current', 'true') : t.removeAttribute('aria-current')));
      const active = this.thumbs[i];
      if (active && active.parentElement && active.closest('[data-gallery-thumbs]')) {
        const list = active.closest('[data-gallery-thumbs]');
        const li = active.parentElement;
        if (list.scrollWidth > list.clientWidth) list.scrollTo({ left: li.offsetLeft - list.firstElementChild.offsetLeft - list.clientWidth / 2 + li.clientWidth / 2, behavior: 'auto' });
      }
      if (this.current) this.current.textContent = utils.num(i + 1);
      if (this.live) this.live.textContent = `${utils.num(i + 1)} / ${utils.num(this.slides.length)}`;
    }
  }
  theme.define('product-gallery', ProductGallery);

  /* ==========================================================================
     عارض الصور بملء الشاشة (تكبير المعرض + اقرأ عينة) — 04 §14
     ========================================================================== */
  class MediaViewer extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.track = this.querySelector('[data-viewer-track]');
      if (!this.dialog || !this.track) return;
      this.slides = Array.from(this.track.children);
      this.current = this.querySelector('[data-viewer-current]');
      this.live = this.querySelector('[data-viewer-live]');
      this.index = 0;
      this.dialog.addEventListener('click', (e) => {
        if (e.target === this.dialog || e.target.closest('[data-viewer-close]')) this.close();
        const z = e.target.closest('[data-viewer-zoom]');
        if (z) this.toggleZoom(z);
      });
      this.dialog.addEventListener('close', () => {
        lockScroll(false);
        this.slides.forEach((s) => this.unzoom(s));
        if (this.opener && document.contains(this.opener)) this.opener.focus();
      });
      this.dialog.addEventListener('keydown', (e) => {
        const rtl = getComputedStyle(this.dialog).direction === 'rtl';
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.go(this.index + (rtl ? 1 : -1)); }
        if (e.key === 'ArrowRight') { e.preventDefault(); this.go(this.index + (rtl ? -1 : 1)); }
      });
      const prev = this.querySelector('[data-viewer-prev]');
      const next = this.querySelector('[data-viewer-next]');
      if (prev) prev.addEventListener('click', () => this.go(this.index - 1));
      if (next) next.addEventListener('click', () => this.go(this.index + 1));
      this.track.addEventListener('scroll', utils.debounce(() => {
        const i = Math.round(Math.abs(this.track.scrollLeft) / (this.track.clientWidth || 1));
        if (i !== this.index) this.update(Math.min(i, this.slides.length - 1));
      }, 60), { passive: true });
    }
    open(i = 0, opener) {
      this.opener = opener || document.activeElement;
      this.slides.forEach((s) => s.querySelectorAll('img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; }));
      this.dialog.showModal();
      lockScroll(true);
      this.go(i, false);
      const btn = this.slides[this.index] && this.slides[this.index].querySelector('[data-viewer-zoom]');
      const close = this.querySelector('[data-viewer-close]');
      (close || btn).focus({ preventScroll: true });
    }
    close() { if (this.dialog.open) this.dialog.close(); }
    go(i, smooth = true) {
      if (!this.slides.length) return;
      const n = (i + this.slides.length) % this.slides.length;
      if (this.slides[this.index]) this.unzoom(this.slides[this.index]);
      this.track.scrollTo({ left: slideOffset(this.track, this.slides[n]), behavior: smooth && !utils.reducedMotion() ? 'smooth' : 'auto' });
      this.update(n);
    }
    update(i) {
      this.index = i;
      if (this.current) this.current.textContent = utils.num(i + 1);
      if (this.live) this.live.textContent = `${utils.num(i + 1)} / ${utils.num(this.slides.length)}`;
    }
    toggleZoom(btn) {
      const slide = btn.closest('.mviewer__slide');
      const on = !slide.classList.contains('is-zoomed');
      if (!on) return this.unzoom(slide);
      slide.classList.add('is-zoomed');
      this.dialog.classList.add('is-zoomed');
      btn.setAttribute('aria-pressed', 'true');
      requestAnimationFrame(() => { slide.scrollTop = (slide.scrollHeight - slide.clientHeight) / 2; slide.scrollLeft = -(slide.scrollWidth - slide.clientWidth) / 2; });
    }
    unzoom(slide) {
      slide.classList.remove('is-zoomed');
      this.dialog.classList.remove('is-zoomed');
      const b = slide.querySelector('[data-viewer-zoom]');
      if (b) b.setAttribute('aria-pressed', 'false');
    }
  }
  theme.define('media-viewer', MediaViewer);
  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-viewer-open]');
    if (!opener) return;
    const dlg = document.getElementById(opener.dataset.viewerOpen);
    const host = dlg && dlg.closest('media-viewer');
    if (host && host.open) { e.preventDefault(); host.open(0, opener); }
  });

  /* ==========================================================================
     معلومات المنتج: النسخ ← السعر، والأزرار، والرابط، والمعرض، وشريط الشراء (05 §4)
     ========================================================================== */
  class ProductInfo extends HTMLElement {
    connectedCallback() {
      this.sid = this.dataset.section;
      const data = document.querySelector(`script[data-variants="${this.sid}"]`);
      try { this.variants = data ? JSON.parse(data.textContent) : []; } catch (e) { this.variants = []; }
      this.picker = this.querySelector('[data-variant-picker]');
      this.select = this.querySelector('[data-variant-select]');
      if (this.picker) {
        this.picker.addEventListener('change', (e) => this.onPick(e));
        this.refreshAvailability();
      }
      this.addEventListener('click', (e) => {
        const pick = e.target.closest('[data-pick-option]');
        if (!pick) return;
        this.pickValue(Number(pick.dataset.pickOption), pick.dataset.pickValue);
        const dlg = pick.closest('dialog');
        if (dlg) dlg.close();
      });
      // «كن أول من يقيّم» يختفي إذا ما فيه قسم مراجعات في الصفحة
      this.querySelectorAll('.pbuy__first-review').forEach((a) => {
        const id = decodeURIComponent((a.getAttribute('href') || '').slice(1));
        if (!id || !document.getElementById(id)) a.closest('.pbuy__rating').hidden = true;
      });
    }
    selectedOptions() {
      return Array.from(this.picker.querySelectorAll('[data-option-index]')).map((fs) => {
        const c = fs.querySelector('input:checked');
        return c ? c.value : null;
      });
    }
    pickValue(index, value) {
      const fs = this.picker && this.picker.querySelector(`[data-option-index="${index}"]`);
      if (!fs) return;
      const input = Array.from(fs.querySelectorAll('input')).find((i) => i.value === value);
      if (!input) return;
      input.checked = true;
      this.onPick({ target: input });
      input.focus();
    }
    onPick(e) {
      const changed = e.target.closest('[data-option-index]');
      let opts = this.selectedOptions();
      let v = this.variants.find((x) => x.options.every((o, i) => o === opts[i]));
      // تركيبة غير متاحة: نختار أول نسخة متاحة فيها القيمة اللي غيّرها الزبون
      if ((!v || !v.available) && changed) {
        const idx = Number(changed.dataset.optionIndex);
        const alt = this.variants.find((x) => x.available && x.options[idx] === opts[idx]);
        if (alt) {
          v = alt;
          alt.options.forEach((val, i) => {
            const fs = this.picker.querySelector(`[data-option-index="${i}"]`);
            const input = fs && Array.from(fs.querySelectorAll('input')).find((x) => x.value === val);
            if (input) input.checked = true;
          });
          opts = this.selectedOptions();
        }
      }
      this.picker.querySelectorAll('[data-option-index]').forEach((fs, i) => {
        const cur = fs.querySelector('[data-option-current]');
        if (cur) cur.textContent = opts[i] || '';
      });
      this.refreshAvailability();
      this.setVariant(v || null);
    }
    refreshAvailability() {
      const opts = this.selectedOptions();
      const hide = this.picker.dataset.unavailable === 'hidden';
      const single = this.picker.querySelectorAll('[data-option-index]').length === 1;
      this.picker.querySelectorAll('[data-option-index]').forEach((fs, i) => {
        fs.querySelectorAll('.vpicker__value').forEach((label) => {
          const input = label.querySelector('input');
          const ok = this.variants.some((x) => x.available && x.options[i] === input.value && x.options.every((o, j) => j === i || o === opts[j]));
          label.classList.toggle('is-unavailable', !ok);
          if (ok || !single) label.removeAttribute('aria-disabled'); else label.setAttribute('aria-disabled', 'true');
          label.hidden = !ok && hide && !input.checked;
          if (single) input.disabled = !ok;
          const tag = label.querySelector('.vpicker__un');
          if (tag) tag.hidden = ok;
        });
      });
    }
    setVariant(v) {
      const sid = this.sid;
      const buttons = document.querySelectorAll(`product-info[data-section="${sid}"] [data-add-button], sticky-buy-bar[data-section="${sid}"] [data-add-button]`);
      const ds = this.dataset;
      let label = ds.labelUnavailable;
      if (v && v.available) label = v.price === 0 ? ds.labelFree : ds.labelAdd;
      else if (v) label = ds.labelSoldout;
      buttons.forEach((b) => {
        const l = b.querySelector('[data-add-label]');
        if (l) l.textContent = label;
        b.disabled = !(v && v.available);
        if (v && v.available) b.removeAttribute('aria-disabled'); else b.setAttribute('aria-disabled', 'true');
      });
      if (!v) return;
      if (this.select) this.select.value = String(v.id);
      document.querySelectorAll(`[data-product-price="${sid}"]`).forEach((el) => { el.innerHTML = v.price_html; });
      document.querySelectorAll(`sticky-buy-bar[data-section="${sid}"] [data-buybar-variant]`).forEach((el) => { el.textContent = v.title; });
      if (v.media) {
        const g = this.querySelector('product-gallery');
        if (g && g.goToMedia) g.goToMedia(v.media);
      }
      const url = new URL(window.location.href);
      url.searchParams.set('variant', v.id);
      try { window.history.replaceState(window.history.state, '', url.toString()); } catch (e) { /* file:// */ }
      this.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant: v } }));
    }
  }
  theme.define('product-info', ProductInfo);

  /* ==========================================================================
     شريط الشراء الثابت — يظهر بعد تجاوز زر الإضافة فقط (04 §12.2)
     ========================================================================== */
  class StickyBuyBar extends HTMLElement {
    connectedCallback() {
      const anchor = document.querySelector(`product-info[data-section="${this.dataset.section}"] [data-buy-anchor]`);
      if (!anchor || !('IntersectionObserver' in window)) return;
      this.io = new IntersectionObserver(([entry]) => {
        const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.toggle(show);
      }, { threshold: 0 });
      this.io.observe(anchor);
    }
    toggle(show) {
      if (this.hidden === !show) return;
      this.hidden = !show;
      root.classList.toggle('has-buy-bar', show);
      if (!show && this.contains(document.activeElement)) document.activeElement.blur();
    }
    disconnectedCallback() {
      if (this.io) this.io.disconnect();
      root.classList.remove('has-buy-bar');
    }
  }
  theme.define('sticky-buy-bar', StickyBuyBar);

  /* ==========================================================================
     «منتجات قد تناسبك» — تُجلب لما يقرب القسم من الشاشة (Recommendations API + Section Rendering)
     ========================================================================== */
  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.children.length && this.querySelector('li')) return;
      const load = async () => {
        try {
          const res = await fetch(this.dataset.url, { headers: { Accept: 'text/html' } });
          if (!res.ok) return;
          const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
          const fresh = doc.querySelector('product-recommendations');
          if (fresh && fresh.querySelector('li')) {
            this.innerHTML = fresh.innerHTML;
            this.hidden = false;
          }
        } catch (e) { /* القسم يبقى مخفيًا */ }
      };
      if (!('IntersectionObserver' in window)) { load(); return; }
      const io = new IntersectionObserver((entries) => {
        if (entries.some((x) => x.isIntersecting)) { io.disconnect(); load(); }
      }, { rootMargin: '0px 0px 400px 0px' });
      io.observe(this.closest('.shopify-section') || this);
    }
  }
  theme.define('product-recommendations', ProductRecommendations);

  /* ==========================================================================
     نتائج القسم/الباقات: الفلاتر، والفرز، و«عرض المزيد»، والبحث الداخلي (04 §14، 05 §6)
     كل شي يشتغل بدون JS (نماذج GET وروابط حقيقية). مع JS: Section Rendering بدون إعادة تحميل + تحديث الرابط.
     ========================================================================== */
  const SCo = () => S().collection || {};
  class CollectionResults extends HTMLElement {
    connectedCallback() {
      this.sid = this.dataset.section;
      this.onInput = utils.debounce(() => this.filterWithin(), 120);
      this.onFacet = utils.debounce(() => this.applyForm(), 450);
      this.addEventListener('change', (e) => {
        if (e.target.closest('[data-sort-select]')) { this.navigate(this.urlFromForm(e.target.form)); return; }
        if (e.target.closest('[data-facets-form]') && (e.target.type === 'checkbox' || e.target.type === 'number')) this.onFacet();
      });
      this.applyView();
      this.watchInfinite();
      // المتصفح ما يرجّع التمرير لحاله في صفحات «عرض المزيد»: نرجّعه نحن بعد إضافة الصفحات السابقة
      if ((this.dataset.paginate === 'load_more' || this.dataset.paginate === 'infinite') && 'scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';
      this.restorePages();
      window.addEventListener('pagehide', () => utils.storage.set(`maktaba:scroll:${window.location.href}`, window.scrollY, 'session'));
      this.addEventListener('input', (e) => { if (e.target.matches('[data-within-search]')) this.onInput(); });
      this.addEventListener('submit', (e) => {
        const f = e.target.closest('[data-facets-form], [data-sort-form]');
        if (!f) return;
        e.preventDefault();
        this.applyForm(f);
      });
      this.addEventListener('click', (e) => {
        const v = e.target.closest('[data-view]');
        if (v) { utils.storage.set('maktaba:view', v.dataset.view); this.applyView(); return; }
        const a = e.target.closest('[data-facet-link]');
        if (a) { e.preventDefault(); this.closeSheet(); this.navigate(a.href); return; }
        const more = e.target.closest('[data-load-more]');
        if (more) { e.preventDefault(); this.loadMore(more); }
      });
      if (!CollectionResults.popBound) {
        CollectionResults.popBound = true;
        window.addEventListener('popstate', () => {
          document.querySelectorAll('collection-results').forEach((el) => el.navigate(window.location.href, false));
        });
      }
    }
    urlFromForm(form) {
      const u = new URL(form.action || this.dataset.url, window.location.origin);
      const params = new URLSearchParams(new FormData(form));
      // حقول السعر الفاضية ما ترسل
      [...params.keys()].forEach((k) => { if (params.get(k) === '') params.delete(k); });
      u.search = params.toString();
      return u.toString();
    }
    applyForm(form) {
      const f = form || this.querySelector('[data-facets-form]');
      if (!f) return;
      if (f.matches('[data-facets-form]')) {
        // الفرز الحالي يبقى
        const sort = this.querySelector('[data-sort-select]');
        if (sort && !f.querySelector('[name="sort_by"]')) {
          const h = document.createElement('input');
          h.type = 'hidden'; h.name = 'sort_by'; h.value = sort.value;
          f.appendChild(h);
        }
      }
      const keepOpen = f.matches('[data-facets-form]') && f.closest('dialog') && f.closest('dialog').open && !form;
      if (!keepOpen) this.closeSheet();
      this.navigate(this.urlFromForm(f), true, keepOpen);
    }
    closeSheet() {
      const d = this.querySelector('dialog[open]');
      if (d) d.close();
    }
    async navigate(href, push = true, keepOpen = false) {
      const u = new URL(href, window.location.origin);
      u.searchParams.delete('page');
      const fetchUrl = new URL(u);
      fetchUrl.searchParams.set('section_id', this.sid);
      this.classList.add('is-loading');
      this.setAttribute('aria-busy', 'true');
      try {
        const res = await fetch(fetchUrl.toString(), { headers: { Accept: 'text/html' } });
        if (!res.ok) throw new Error(String(res.status));
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const fresh = doc.querySelector(`collection-results[data-section="${this.sid}"]`);
        if (!fresh) throw new Error('no-results');
        const openId = keepOpen && this.querySelector('dialog[open]') ? this.querySelector('dialog[open]').id : null;
        const focusId = document.activeElement && this.contains(document.activeElement) ? document.activeElement.id : null;
        if (!openId) this.closeSheet();
        if (openId) {
          // النافذة مفتوحة: نحدّث محتواها بدل ما نقفلها (أعداد القيم تتغير)
          const oldBody = this.querySelector(`#${CSS.escape(openId)} .fsheet__body`);
          const newBody = fresh.querySelector(`#${CSS.escape(openId)} .fsheet__body`);
          if (oldBody && newBody) oldBody.replaceWith(newBody);
          ['.ctoolbar__count', '.factive', '[data-results-body]', '.ftoggle__count'].forEach((sel) => {
            const o = this.querySelector(`:scope ${sel}`);
            const n = fresh.querySelector(sel);
            if (o && n) o.replaceWith(n); else if (o && !n) o.remove();
            else if (!o && n && sel === '.factive') { const tb = this.querySelector('.ctoolbar'); if (tb) tb.after(n); }
          });
        } else {
          this.innerHTML = fresh.innerHTML;
        }
        this.applyView();
        this.watchInfinite();
        if (push) window.history.pushState({ collection: true }, '', u.toString());
        const count = this.querySelector('[data-results-count]');
        const live = this.querySelector('[data-results-live]');
        if (live && count) live.textContent = (SCo().updated || '').replace('__COUNT__', count.textContent.trim());
        const focusBack = focusId && document.getElementById(focusId);
        if (focusBack) focusBack.focus({ preventScroll: true });
        else if (!openId && count) { count.setAttribute('tabindex', '-1'); count.focus({ preventScroll: true }); }
        if (!openId) {
          const top = this.getBoundingClientRect().top;
          if (top < 0) this.scrollIntoView({ block: 'start', behavior: utils.reducedMotion() ? 'auto' : 'smooth' });
        }
      } catch (e) {
        window.location.href = u.toString();
      } finally {
        this.classList.remove('is-loading');
        this.removeAttribute('aria-busy');
      }
    }
    async loadMore(btn) {
      if (btn.getAttribute('aria-busy') === 'true') return;
      utils.setBusy(btn, true, SCo().loading);
      try {
        const u = new URL(btn.href, window.location.origin);
        u.searchParams.set('section_id', this.sid);
        const res = await fetch(u.toString(), { headers: { Accept: 'text/html' } });
        if (!res.ok) throw new Error(String(res.status));
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const items = doc.querySelectorAll('[data-results-list] > li');
        const list = this.querySelector('[data-results-list]');
        const firstNew = items[0];
        items.forEach((li) => list.appendChild(document.importNode(li, true)));
        const newMore = doc.querySelector('.cmore');
        const oldMore = this.querySelector('.cmore');
        if (newMore) oldMore.replaceWith(document.importNode(newMore, true)); else oldMore.remove();
        const pageUrl = new URL(btn.href, window.location.origin);
        window.history.replaceState(window.history.state, '', pageUrl.toString());
        this.filterWithin();
        this.watchInfinite();
        // التركيز على أول كرت جديد (لوحة المفاتيح) — ما نحرك التركيز في التحميل التلقائي
        if (btn.hasAttribute('data-auto')) return;
        const added = firstNew && list.querySelector(`li:nth-child(${list.children.length - items.length + 1}) a`);
        if (added) added.focus({ preventScroll: false });
      } catch (e) {
        window.location.href = btn.href;
      }
    }
    applyView() {
      const list = this.querySelector('[data-results-list]');
      const btns = this.querySelectorAll('[data-view]');
      if (!list || !btns.length) return;
      const view = utils.storage.get('maktaba:view') === 'list' ? 'list' : 'grid';
      list.classList.toggle('cgrid--list', view === 'list');
      btns.forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.view === view)));
    }
    watchInfinite() {
      if (this.io) this.io.disconnect();
      const more = this.querySelector('[data-load-more][data-auto]');
      if (!more || !('IntersectionObserver' in window)) return;
      this.io = new IntersectionObserver((entries) => {
        if (entries.some((x) => x.isIntersecting)) { this.io.disconnect(); this.loadMore(more); }
      }, { rootMargin: '0px 0px 600px 0px' });
      this.io.observe(more);
    }
    /* «عرض المزيد» مع الرجوع: إذا الرابط ?page=3 نرجّع الصفحات 1 و2 قبلها، ثم مكان الزبون */
    async restorePages() {
      const mode = this.dataset.paginate;
      const page = Number(this.dataset.page || 1);
      const list = this.querySelector('[data-results-list]');
      if (!list || page < 2 || (mode !== 'load_more' && mode !== 'infinite')) return;
      const saved = utils.storage.get(`maktaba:scroll:${window.location.href}`, 'session');
      if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';
      const frag = document.createDocumentFragment();
      try {
        for (let p = 1; p < page; p += 1) {
          const u = new URL(window.location.href);
          u.searchParams.set('page', p);
          u.searchParams.set('section_id', this.sid);
          const res = await fetch(u.toString(), { headers: { Accept: 'text/html' } });
          if (!res.ok) throw new Error(String(res.status));
          const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
          doc.querySelectorAll('[data-results-list] > li:not(.cgrid__wide)').forEach((li) => frag.appendChild(document.importNode(li, true)));
        }
        list.prepend(frag);
        this.filterWithin();
        if (typeof saved === 'number') {
          window.scrollTo(0, saved);
          requestAnimationFrame(() => window.scrollTo(0, saved));
        }
      } catch (e) { /* تبقى الصفحة الحالية فقط */ }
    }
    filterWithin() {
      const input = this.querySelector('[data-within-search]');
      if (!input) return;
      const q = utils.normalizeArabic(input.value).toLowerCase();
      let shown = 0;
      this.querySelectorAll('[data-results-list] > li').forEach((li) => {
        const ok = !q || utils.normalizeArabic(li.dataset.title || '').toLowerCase().includes(q);
        li.hidden = !ok;
        if (ok) shown += 1;
      });
      const empty = this.querySelector('[data-within-empty]');
      if (empty) {
        empty.hidden = shown > 0 || !q;
        const term = input.value.trim();
        empty.textContent = ((this.dataset.paginate ? SCo().withinEmpty : SCo().noMatch) || '').replace('__TERMS__', term);
        if (this.dataset.paginate && term) {
          const a = document.createElement('a');
          a.href = `${theme.routes.search}?q=${encodeURIComponent(utils.normalizeArabic(term))}`;
          a.className = 'cresults__everywhere';
          a.textContent = (SCo().searchAll || '').replace('__TERMS__', term);
          empty.append(' ', a);
        }
      }
      const more = this.querySelector('.cmore');
      if (more) more.hidden = !!q;
    }
  }
  theme.define('collection-results', CollectionResults);

  /* البحث: نشيل التشكيل والكشيدة من عبارة البحث قبل الإرسال (مرجع الكود) — الهمزات ما تتغير */
  document.addEventListener('submit', (e) => {
    const f = e.target;
    if (!f.matches || !f.matches('form[role="search"], form[action$="/search"]')) return;
    const q = f.querySelector('input[name="q"]');
    if (q && q.value) q.value = utils.normalizeArabic(q.value);
  }, true);

  /* ==========================================================================
     شريط الإعلان — تبديل هادئ + إيقاف + احترام تقليل الحركة
     ========================================================================== */
  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.msgs = Array.from(this.querySelectorAll('.announcement__msg'));
      if (this.msgs.length < 2) return;
      this.index = 0;
      this.interval = (Number(this.dataset.interval) || 5) * 1000;
      this.button = this.querySelector('[data-pause]');
      this.userPaused = utils.reducedMotion();
      this.updateButton();
      this.button.addEventListener('click', () => {
        this.userPaused = !this.userPaused;
        this.updateButton();
        this.userPaused ? this.stop() : this.start();
      });
      this.addEventListener('mouseenter', () => this.stop());
      this.addEventListener('mouseleave', () => { if (!this.userPaused) this.start(); });
      this.addEventListener('focusin', () => this.stop());
      this.addEventListener('focusout', () => { if (!this.userPaused) this.start(); });
      if (!this.userPaused) this.start();
    }
    updateButton() {
      this.button.setAttribute('aria-pressed', String(this.userPaused));
      this.button.setAttribute('aria-label', this.userPaused ? this.button.dataset.labelPlay : this.button.dataset.labelPause);
      this.button.querySelector('.announcement__icon-pause').hidden = this.userPaused;
      this.button.querySelector('.announcement__icon-play').hidden = !this.userPaused;
    }
    start() { this.stop(); this.timer = setInterval(() => this.next(), this.interval); }
    stop() { clearInterval(this.timer); }
    next() {
      const current = this.msgs[this.index];
      this.index = (this.index + 1) % this.msgs.length;
      const nextMsg = this.msgs[this.index];
      current.classList.add('is-fading');
      setTimeout(() => {
        current.hidden = true;
        current.classList.remove('is-fading', 'is-active');
        nextMsg.hidden = false;
        nextMsg.classList.add('is-active');
      }, utils.reducedMotion() ? 0 : 320);
    }
    disconnectedCallback() { this.stop(); }
  }
  theme.define('announcement-bar', AnnouncementBar);

  /* ==========================================================================
     الهيدر — الأوضاع الأربعة (04 §2.3)
     ========================================================================== */
  class StickyHeader extends HTMLElement {
    connectedCallback() {
      this.mode = this.dataset.mode;
      this.header = this.querySelector('.header');
      this.lastY = window.scrollY;
      this.ticking = false;
      if (this.mode === 'static') { root.style.setProperty('--header-sticky-h', '0px'); return; }
      this.onScroll = () => {
        if (this.ticking) return;
        this.ticking = true;
        requestAnimationFrame(() => { this.update(); this.ticking = false; });
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', utils.debounce(() => this.measure(), 150));
      this.measure();
      this.update();
    }
    measure() {
      if (this.classList.contains('is-collapsed')) return;
      this.fullHeight = this.header.offsetHeight;
      if (this.mode !== 'collapse') root.style.setProperty('--header-sticky-h', `${this.fullHeight}px`);
    }
    update() {
      const y = window.scrollY;
      if (this.mode === 'collapse') {
        const bottom = this.getBoundingClientRect().top + (this.fullHeight || this.header.offsetHeight);
        const shouldCollapse = bottom <= 0;
        if (shouldCollapse !== this.classList.contains('is-collapsed')) {
          if (shouldCollapse) this.style.minHeight = `${this.fullHeight}px`;
          this.classList.toggle('is-collapsed', shouldCollapse);
          if (!shouldCollapse) this.style.minHeight = '';
          root.classList.toggle('header-is-collapsed', shouldCollapse);
          requestAnimationFrame(() => {
            root.style.setProperty('--header-sticky-h', `${shouldCollapse ? this.header.offsetHeight : 0}px`);
          });
        }
      } else if (this.mode === 'reveal') {
        const goingDown = y > this.lastY;
        const hide = goingDown && y > this.fullHeight * 1.5 && !this.contains(document.activeElement);
        this.classList.toggle('is-hidden', hide);
        root.style.setProperty('--header-sticky-h', hide ? '0px' : `${this.fullHeight}px`);
      }
      this.lastY = y;
    }
    disconnectedCallback() { window.removeEventListener('scroll', this.onScroll); }
  }
  theme.define('sticky-header', StickyHeader);

  /* ==========================================================================
     الروابط السريعة — «المزيد ▾» في التابلت والويب، وإشارة السحب في الجوال
     ========================================================================== */
  class QuickLinks extends HTMLElement {
    connectedCallback() {
      this.list = this.querySelector('.quick-links__list');
      this.more = this.querySelector('.quick-links__more');
      this.overflow = this.querySelector('.quick-links__overflow');
      this.items = Array.from(this.list.querySelectorAll('.quick-links__item'));
      this.layout = utils.debounce(() => this.relayout(), 100);
      this.list.addEventListener('scroll', () => this.updateFade(), { passive: true });
      window.addEventListener('resize', this.layout);
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => this.relayout());
      this.relayout();
    }
    restore() {
      this.items.forEach((li) => this.list.insertBefore(li, this.more));
      this.more.hidden = true;
    }
    relayout() {
      this.restore();
      if (mqMobile.matches) { this.updateFade(); return; }
      this.list.classList.remove('fade-start', 'fade-end');
      if (this.list.scrollWidth <= this.list.clientWidth + 1) return;
      this.more.hidden = false;
      const visible = this.items.slice();
      while (this.list.scrollWidth > this.list.clientWidth + 1 && visible.length > 1) {
        const li = visible.pop();
        this.overflow.prepend(li);
      }
    }
    updateFade() {
      if (!mqMobile.matches) return;
      const max = this.list.scrollWidth - this.list.clientWidth;
      const pos = Math.abs(this.list.scrollLeft);
      this.list.classList.toggle('fade-start', pos > 2);
      this.list.classList.toggle('fade-end', max > 2 && pos < max - 2);
    }
  }
  theme.define('quick-links', QuickLinks);

  /* ==========================================================================
     القائمة الجانبية (Dialog) — 04 §4
     ========================================================================== */
  class MenuDrawer extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.dialog.addEventListener('click', (e) => { if (e.target === this.dialog) this.close(); });
      this.dialog.querySelectorAll('[data-drawer-close]').forEach((b) => b.addEventListener('click', () => this.close()));
      this.dialog.addEventListener('close', () => {
        lockScroll(false);
        document.querySelectorAll(`[aria-controls="${this.dialog.id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'false'));
        if (this.opener && document.contains(this.opener)) this.opener.focus();
      });
    }
    open(opener) {
      this.opener = opener;
      if (this.dialog.open) return;
      this.dialog.showModal();
      lockScroll(true);
      document.querySelectorAll(`[aria-controls="${this.dialog.id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'true'));
    }
    close() { if (this.dialog.open) this.dialog.close(); }
  }
  theme.define('menu-drawer', MenuDrawer);
  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-drawer-open]');
    if (!opener) return;
    const dialog = document.getElementById(opener.dataset.drawerOpen);
    const host = dialog && dialog.closest('menu-drawer');
    if (host && host.open) { e.preventDefault(); host.open(opener); }
  });

  /* ==========================================================================
     البحث الفوري — 04 §6 (ARIA combobox + كيبورد + إلغاء الطلب القديم + تطبيع عربي)
     ========================================================================== */
  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.panel = this.querySelector('.psearch__panel');
      this.results = this.querySelector('[data-results]');
      this.initial = this.querySelector('[data-initial]');
      this.status = this.querySelector('[data-status]');
      this.clearBtn = this.querySelector('[type="reset"]');
      this.min = Number(this.dataset.min) || 2;
      this.cache = new Map();
      this.active = -1;
      this.onInput = utils.debounce(() => this.search(), 250);

      this.input.addEventListener('input', () => { this.clearBtn.hidden = !this.input.value; this.onInput(); });
      this.input.addEventListener('focus', () => this.onFocus());
      this.input.addEventListener('keydown', (e) => this.onKey(e));
      this.clearBtn.addEventListener('click', () => {
        setTimeout(() => { this.clearBtn.hidden = true; this.results.innerHTML = ''; this.showInitial(); this.input.focus(); });
      });
      this.querySelectorAll('[data-search-close]').forEach((b) => b.addEventListener('click', () => this.close(true)));
      document.addEventListener('click', (e) => { if (!this.contains(e.target) && !e.target.closest('[data-search-open]')) this.close(); });
      this.addEventListener('focusout', (e) => {
        if (this.classList.contains('is-overlay')) return;
        if (!this.contains(e.relatedTarget)) setTimeout(() => { if (!this.contains(document.activeElement)) this.close(); }, 0);
      });
    }
    onFocus() {
      if (mqTouchNav.matches && !this.classList.contains('is-overlay')) {
        this.classList.add('is-overlay');
        lockScroll(true);
      }
      if (this.input.value.trim().length >= this.min) this.search();
      else this.showInitial();
    }
    openPanel() {
      this.panel.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
    }
    close(fromBack) {
      this.panel.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
      this.active = -1;
      if (this.classList.contains('is-overlay')) {
        this.classList.remove('is-overlay');
        lockScroll(false);
        if (fromBack) this.input.blur();
      }
    }
    showInitial() {
      this.results.innerHTML = '';
      if (this.initial) { this.initial.hidden = false; this.openPanel(); }
      else if (!this.classList.contains('is-overlay')) this.close();
      else this.openPanel();
    }
    /** الطريقة: «suggest» (Predictive Search) للغات المدعومة فقط، و«search» (البحث الكامل) للعربي وغيره */
    get mode() {
      if (PredictiveSearch.mode) return PredictiveSearch.mode;
      const lang = (document.documentElement.lang || 'ar').slice(0, 2).toLowerCase();
      PredictiveSearch.mode = PredictiveSearch.SUPPORTED.includes(lang) ? 'suggest' : 'search';
      return PredictiveSearch.mode;
    }
    buildUrl(q) {
      const section = this.dataset.section || 'predictive-search';
      if (this.mode === 'suggest') {
        const p = new URLSearchParams({
          q,
          'resources[type]': 'product,collection,article,query',
          'resources[limit]': '10',
          'resources[limit_scope]': 'each',
          'resources[options][unavailable_products]': 'last',
          section_id: section
        });
        return `${theme.routes.predictiveSearch}?${p}`;
      }
      const p = new URLSearchParams({ q, type: 'product,article', 'options[prefix]': 'last', 'options[unavailable_products]': 'last', section_id: section });
      return `${theme.routes.search}?${p}`;
    }
    async search() {
      const raw = this.input.value;
      const q = utils.normalizeArabic(raw);
      if (q.length < this.min) { this.showInitial(); return; }
      if (this.initial) this.initial.hidden = true;
      this.openPanel();
      if (this.cache.has(q)) { this.render(this.cache.get(q), q); return; }
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      this.classList.add('is-loading');
      this.results.setAttribute('aria-busy', 'true');
      this.results.innerHTML = '<div class="psearch__loading">' + '<div class="skeleton skeleton--row" aria-hidden="true"><span class="skeleton__media"></span><span class="skeleton__line"></span><span class="skeleton__line skeleton__line--short"></span></div>'.repeat(3) + '</div>';
      const url = this.buildUrl(q);
      try {
        let res = await fetch(url, { signal: this.controller.signal });
        if (res.status === 417 && this.mode === 'suggest') {
          // اللغة غير مدعومة في Predictive Search → نتحول للبحث الكامل (يدعم العربي)
          PredictiveSearch.mode = 'search';
          res = await fetch(this.buildUrl(q), { signal: this.controller.signal });
        }
        if (!res.ok) throw new Error(String(res.status));
        const text = await res.text();
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const inner = doc.querySelector('.psearch__results-inner');
        const html = inner ? inner.outerHTML : '';
        this.cache.set(q, html);
        this.render(html, q);
      } catch (err) {
        if (err.name === 'AbortError') return;
        this.results.innerHTML = `<div class="state state-error" role="alert"><p class="state__text">${escapeHTML(S().searchError)}</p><button type="button" class="btn btn--s btn--outline" data-retry>${escapeHTML(S().retry)}</button></div>`;
        this.results.querySelector('[data-retry]').addEventListener('click', () => this.search());
      } finally {
        this.classList.remove('is-loading');
        this.results.removeAttribute('aria-busy');
      }
    }
    render(html, q) {
      this.results.innerHTML = html;
      const words = q.split(' ').filter((w) => w.length > 0).map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
      if (words.length) {
        const re = new RegExp(`(${words.join('|')})`, 'gi');
        this.results.querySelectorAll('[data-hl-text]').forEach((el) => {
          el.innerHTML = escapeHTML(el.textContent).replace(re, '<mark>$1</mark>');
        });
      }
      this.options = Array.from(this.results.querySelectorAll('[role="option"]'));
      this.active = -1;
      const listbox = this.results.querySelector('[role="listbox"]');
      if (listbox) this.input.setAttribute('aria-controls', listbox.id);
      const count = this.results.querySelector('[data-count]');
      if (this.status && count) this.status.textContent = count.textContent;
    }
    onKey(e) {
      if (e.key === 'Escape') { e.preventDefault(); this.close(true); return; }
      if (!this.options || !this.options.length || this.panel.hidden) return;
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        const dir = e.key === 'ArrowDown' ? 1 : -1;
        this.active = (this.active + dir + this.options.length) % this.options.length;
        this.options.forEach((o, i) => o.setAttribute('aria-selected', String(i === this.active)));
        const cur = this.options[this.active];
        this.input.setAttribute('aria-activedescendant', cur.id);
        cur.scrollIntoView({ block: 'nearest' });
      } else if (e.key === 'Enter' && this.active > -1) {
        e.preventDefault();
        window.location.href = this.options[this.active].href;
      }
    }
  }
  PredictiveSearch.SUPPORTED = ['af', 'sq', 'hy', 'bs', 'bg', 'ca', 'hr', 'cs', 'da', 'nl', 'en', 'et', 'fo', 'fi', 'fr', 'gd', 'de', 'el', 'hu', 'is', 'id', 'it', 'la', 'lv', 'lt', 'mk', 'mo', 'nb', 'nn', 'no', 'pl', 'pt', 'ro', 'ru', 'sr', 'sh', 'sk', 'sl', 'es', 'sv', 'tr', 'uk', 'vi', 'cy'];
  PredictiveSearch.mode = null;
  theme.define('predictive-search', PredictiveSearch);
  document.addEventListener('click', (e) => {
    const t = e.target.closest('[data-search-open]');
    if (!t) return;
    const scope = t.closest('.header') || document;
    const ps = scope.querySelector('predictive-search');
    if (ps && ps.input) {
      ps.classList.add('is-overlay');
      lockScroll(true);
      ps.input.focus();
    }
  });

  /* ==========================================================================
     محدد العملة — إغلاق بـ Escape، والإرسال من شوبيفاي نفسها
     ========================================================================== */
  class CurrencySelector extends HTMLElement {
    connectedCallback() {
      this.details = this.querySelector('details');
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.details.open) { this.details.open = false; this.querySelector('summary').focus(); }
      });
      this.querySelector('form').addEventListener('submit', (e) => {
        const btn = e.submitter;
        if (btn) btn.setAttribute('aria-busy', 'true');
        this.failTimer = setTimeout(() => theme.toast(S().currencyError, { type: 'error' }), 8000);
      });
    }
  }
  theme.define('currency-selector', CurrencySelector);

  /* ==========================================================================
     الفوتر: Accordion في الجوال فقط
     ========================================================================== */
  function initFooter(scope = document) {
    scope.querySelectorAll('.footer').forEach((footer) => {
      const accordion = footer.classList.contains('footer--mobile-accordion');
      const groups = footer.querySelectorAll('.footer__group');
      const apply = () => groups.forEach((d) => { d.open = !(accordion && mqMobile.matches); });
      apply();
      mqMobile.addEventListener('change', apply);
      groups.forEach((d) => d.querySelector('summary').addEventListener('click', (e) => {
        if (!(accordion && mqMobile.matches)) e.preventDefault();
      }));
    });
  }

  /* ==========================================================================
     لوحة الشطرنج: عدد الأعمدة الفعلي لكل مقاس (مزلق 8)
     ========================================================================== */
  const checkerRO = 'ResizeObserver' in window ? new ResizeObserver((entries) => entries.forEach((en) => setCols(en.target))) : null;
  function setCols(grid) {
    const cols = getComputedStyle(grid).gridTemplateColumns.split(' ').filter(Boolean).length;
    if (grid.dataset.cols !== String(cols)) grid.dataset.cols = String(cols);
  }
  function initCheckers(scope = document) {
    scope.querySelectorAll('.tiles-checker').forEach((g) => { setCols(g); if (checkerRO) checkerRO.observe(g); });
  }

  /* أزرار تجربة الرسائل في «عرض المكونات» */
  document.addEventListener('click', (e) => {
    const b = e.target.closest('[data-toast-demo]');
    if (!b) return;
    const type = b.dataset.toastDemo;
    theme.toast((S().demoToast || {})[type], { type, action: type === 'success' ? { label: S().viewCart, url: theme.routes.cart } : null });
  });

  /* «إعادة المحاولة» العامة: يعيد تحميل القسم عبر Section Rendering، وإلا الصفحة */
  document.addEventListener('click', async (e) => {
    const b = e.target.closest('[data-retry]');
    if (!b || b.closest('predictive-search')) return;
    const section = b.closest('.shopify-section');
    if (!section || !section.id.startsWith('shopify-section-')) { window.location.reload(); return; }
    utils.setBusy(b, true);
    try {
      const html = await utils.fetchSection(section.id.replace('shopify-section-', ''));
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.getElementById(section.id);
      if (fresh) section.innerHTML = fresh.innerHTML;
    } catch (err) {
      utils.setBusy(b, false);
      theme.toast(S().offline, { type: 'error' });
    }
  });

  /* ---------- التشغيل ---------- */
  /* ==========================================================================
     الواجهة الرئيسية — شرائح بسحب أصلي (scroll-snap) + نقاط + تشغيل تلقائي (05 §1)
     التشغيل التلقائي يتوقف نهائيًا عند أول لمس أو تفاعل، وما يبدأ مع «تقليل الحركة».
     ========================================================================== */
  const isRTL = (el) => getComputedStyle(el).direction === 'rtl';
  /** يمرّر الحاوية حتى يبدأ العنصر من بدايتها (يمين في RTL)، بدون ما يحرّك الصفحة عموديًا */
  function scrollToItem(track, item, smooth = true) {
    const t = track.getBoundingClientRect();
    const r = item.getBoundingClientRect();
    const delta = isRTL(track) ? r.right - t.right : r.left - t.left;
    track.scrollBy({ left: delta, behavior: smooth && !utils.reducedMotion() ? 'smooth' : 'auto' });
  }

  class HeroSlider extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.hero__track');
      this.slides = Array.from(this.querySelectorAll('.hero__slide'));
      this.dots = Array.from(this.querySelectorAll('[data-hero-dot]'));
      this.pauseBtn = this.querySelector('[data-hero-pause]');
      this.index = 0;
      if (this.slides.length < 2) return;
      this.speed = Number(this.dataset.speed) || 6000;
      this.stopped = this.dataset.autoplay !== 'true' || utils.reducedMotion();
      this.visible = true;
      this.dots.forEach((d) => d.addEventListener('click', () => { this.stopForGood(); this.go(Number(d.dataset.heroDot)); }));
      if (this.pauseBtn) this.pauseBtn.addEventListener('click', () => (this.timer ? this.stopForGood() : this.resume()));
      /* أي تفاعل = إيقاف نهائي (لمس، سحب، عجلة، كيبورد، تركيز داخل الشرائح) */
      const halt = () => this.stopForGood();
      ['pointerdown', 'wheel', 'keydown'].forEach((ev) => this.track.addEventListener(ev, halt, { passive: true }));
      this.track.addEventListener('focusin', halt);
      this.track.addEventListener('keydown', (e) => {
        if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
        const forward = (e.key === 'ArrowLeft') === isRTL(this.track);
        this.go(this.index + (forward ? 1 : -1));
        e.preventDefault();
      });
      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver((entries) => {
          entries.forEach((en) => {
            if (en.target === this) { this.visible = en.isIntersecting; this.visible ? this.start() : this.pause(); return; }
            if (en.isIntersecting && en.intersectionRatio > 0.6) this.setActive(this.slides.indexOf(en.target));
          });
        }, { root: null, threshold: [0, 0.6] });
        this.slideIO = new IntersectionObserver((entries) => {
          entries.forEach((en) => { if (en.isIntersecting) this.setActive(this.slides.indexOf(en.target)); });
        }, { root: this.track, threshold: 0.6 });
        this.slides.forEach((sl) => this.slideIO.observe(sl));
        this.io.observe(this);
      }
      this.onResize = utils.debounce(() => (this.isSlider() ? this.start() : this.pause()), 200);
      window.addEventListener('resize', this.onResize);
      this.onVis = () => (document.hidden ? this.pause() : this.start());
      document.addEventListener('visibilitychange', this.onVis);
      this.updatePauseBtn();
      this.start();
    }
    setActive(i) {
      if (i < 0 || i === this.index && this.dots[i]?.getAttribute('aria-current') === 'true') return;
      this.index = i;
      this.dots.forEach((d, k) => (k === i ? d.setAttribute('aria-current', 'true') : d.removeAttribute('aria-current')));
      this.slides.forEach((sl, k) => sl.classList.toggle('is-active', k === i));
    }
    go(i) {
      const n = this.slides.length;
      const target = ((i % n) + n) % n;
      scrollToItem(this.track, this.slides[target]);
      this.setActive(target);
    }
    /** في «Bento» (تابلت وويب) الشرائح كلها ظاهرة، فما فيه تشغيل تلقائي */
    isSlider() { return getComputedStyle(this.track).overflowX === 'auto'; }
    start() {
      if (this.stopped || !this.visible || document.hidden || this.timer || !this.isSlider()) return;
      this.timer = setInterval(() => this.go(this.index + 1), this.speed);
      this.updatePauseBtn();
    }
    pause() { clearInterval(this.timer); this.timer = null; this.updatePauseBtn(); }
    stopForGood() { this.stopped = true; this.pause(); }
    resume() { this.stopped = false; this.start(); }
    updatePauseBtn() {
      if (!this.pauseBtn) return;
      const playing = !!this.timer;
      this.pauseBtn.setAttribute('aria-pressed', String(!playing));
      this.pauseBtn.setAttribute('aria-label', playing ? this.pauseBtn.dataset.labelPause : this.pauseBtn.dataset.labelPlay);
      this.pauseBtn.classList.toggle('is-paused', !playing);
    }
    disconnectedCallback() {
      this.pause();
      if (this.io) this.io.disconnect();
      if (this.slideIO) this.slideIO.disconnect();
      document.removeEventListener('visibilitychange', this.onVis);
      window.removeEventListener('resize', this.onResize);
    }
  }
  theme.define('hero-slider', HeroSlider);

  /* ==========================================================================
     صف بسحب أفقي وأسهم — scroll-rail (الباقات، والمنتجات، والموضوعات، وشاهدتها مؤخرًا)
     data-feed: يجيب الترتيب الصحيح («الأكثر مبيعًا» / «الأحدث») من صفحة القسم عبر card-feed.
     ========================================================================== */
  /** يعيد تسمية الـids داخل جزء مجلوب حتى ما تتكرر في الصفحة، ويحدّث المراجع إليها */
  function uniquifyIds(fragment, prefix) {
    const map = {};
    fragment.querySelectorAll('[id]').forEach((el) => { const n = `${prefix}-${el.id}`; map[el.id] = n; el.id = n; });
    ['aria-labelledby', 'aria-describedby', 'aria-controls', 'for', 'form'].forEach((attr) => {
      fragment.querySelectorAll(`[${attr}]`).forEach((el) => {
        el.setAttribute(attr, el.getAttribute(attr).split(' ').map((id) => map[id] || id).join(' '));
      });
    });
  }
  function adaptCard(li, rail) {
    if (rail.closest('[data-card-size="compact"]') || rail.dataset.cardSize === 'compact') {
      li.querySelectorAll('.card-product').forEach((c) => { c.classList.remove('card--standard'); c.classList.add('card--compact'); });
      li.querySelectorAll('.card-add').forEach((a) => a.remove());
    }
  }

  class ScrollRail extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.rail__track');
      this.nav = this.id ? document.querySelector(`[data-rail-nav="${this.id}"]`) : null;
      this.prev = this.nav && this.nav.querySelector('[data-rail-prev]');
      this.next = this.nav && this.nav.querySelector('[data-rail-next]');
      if (this.prev) this.prev.addEventListener('click', () => this.page(-1));
      if (this.next) this.next.addEventListener('click', () => this.page(1));
      this.update = this.update.bind(this);
      this.track.addEventListener('scroll', utils.debounce(this.update, 60), { passive: true });
      if ('ResizeObserver' in window) { this.ro = new ResizeObserver(this.update); this.ro.observe(this.track); }
      this.update();
      if (this.dataset.feed) this.loadFeed();
    }
    isScroller() { return getComputedStyle(this.track).overflowX === 'auto'; }
    update() {
      if (!this.nav) return;
      const max = this.track.scrollWidth - this.track.clientWidth;
      const scrollable = this.isScroller() && max > 4;
      this.nav.hidden = !scrollable;
      if (!scrollable) return;
      const pos = Math.abs(this.track.scrollLeft);
      this.prev.disabled = pos < 4;
      this.next.disabled = pos > max - 4;
    }
    page(dir) {
      const step = this.track.clientWidth * 0.9 * dir * (isRTL(this.track) ? -1 : 1);
      this.track.scrollBy({ left: step, behavior: utils.reducedMotion() ? 'auto' : 'smooth' });
    }
    async loadFeed() {
      const limit = Number(this.dataset.limit) || 10;
      try {
        const res = await fetch(this.dataset.feed, { headers: { Accept: 'text/html' } });
        if (!res.ok) return;
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const items = Array.from(doc.querySelectorAll('.card-feed > li')).slice(0, limit);
        if (!items.length) return;
        const current = Array.from(this.track.children).map((li) => li.dataset.productId).join(',');
        if (items.map((li) => li.dataset.productId).join(',') === current) return;
        const frag = document.createDocumentFragment();
        const existing = new Map(Array.from(this.track.children).map((li) => [li.dataset.productId, li]));
        items.forEach((li) => {
          const keep = existing.get(li.dataset.productId);
          if (keep) { frag.appendChild(keep); return; }
          const node = document.importNode(li, true);
          uniquifyIds(node, this.dataset.uid || 'rail');
          adaptCard(node, this);
          frag.appendChild(node);
        });
        this.track.replaceChildren(frag);
        this.track.scrollLeft = 0;
        this.update();
      } catch (e) { /* يبقى ترتيب القسم الأصلي */ }
    }
    disconnectedCallback() { if (this.ro) this.ro.disconnect(); }
  }
  theme.define('scroll-rail', ScrollRail);

  /* ==========================================================================
     شاهدتها مؤخرًا — يحفظ آخر المنتجات في متصفح الزائر فقط (بدون بيانات شخصية)
     ========================================================================== */
  const RV_KEY = 'maktaba:viewed';
  const trackViewed = document.querySelector('[data-track-viewed]');
  if (trackViewed) {
    const h = trackViewed.dataset.trackViewed;
    const list = (utils.storage.get(RV_KEY) || []).filter((x) => x !== h);
    list.unshift(h);
    utils.storage.set(RV_KEY, list.slice(0, 16));
  }
  class RecentlyViewed extends HTMLElement {
    async connectedCallback() {
      const limit = Number(this.dataset.limit) || 6;
      const exclude = this.dataset.exclude;
      const handles = (utils.storage.get(RV_KEY) || []).filter((h) => h && h !== exclude).slice(0, limit);
      if (!handles.length) return;
      const track = this.querySelector('[data-rail-track]');
      const results = await Promise.all(handles.map(async (h, i) => {
        try {
          const res = await fetch(`${theme.routes.root.replace(/\/$/, '')}/products/${encodeURIComponent(h)}?section_id=card-feed`, { headers: { Accept: 'text/html' } });
          if (!res.ok) return null;
          const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
          const li = doc.querySelector('.card-feed > li');
          if (li) uniquifyIds(li, `${this.dataset.uid}-${i}`);
          return li ? document.importNode(li, true) : null;
        } catch (e) { return null; }
      }));
      const items = results.filter(Boolean);
      if (!items.length) return;
      track.replaceChildren(...items);
      const note = this.querySelector('[data-recently-note]');
      if (note) note.remove();
      this.hidden = false;
      const rail = this.querySelector('scroll-rail');
      if (rail && rail.update) rail.update();
    }
  }
  theme.define('recently-viewed', RecentlyViewed);

  /* ==========================================================================
     فيديو كسول — الصورة المصغرة أولًا، والمشغل يتحمّل عند الضغط فقط (بلوك «فيديو / تضمين»)
     ========================================================================== */
  class LazyVideo extends HTMLElement {
    connectedCallback() {
      const btn = this.querySelector('.cb-video__play');
      if (!btn) return;
      btn.addEventListener('click', () => {
        const tpl = this.querySelector('template');
        if (tpl) {
          this.appendChild(tpl.content.cloneNode(true));
        } else if (this.dataset.type && this.dataset.id) {
          const f = document.createElement('iframe');
          f.src = this.dataset.type === 'youtube'
            ? `https://www.youtube-nocookie.com/embed/${encodeURIComponent(this.dataset.id)}?autoplay=1&rel=0`
            : `https://player.vimeo.com/video/${encodeURIComponent(this.dataset.id)}?autoplay=1`;
          f.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
          f.title = btn.getAttribute('aria-label') || '';
          this.appendChild(f);
        }
        btn.remove();
        const media = this.querySelector('video, iframe');
        if (media) media.focus();
      }, { once: true });
    }
  }
  theme.define('lazy-video', LazyVideo);

  /* ==========================================================================
     topic-reader — صفحة الموضوع (05 §13): أدوات القراءة، والفهرس، وشريط التقدم، وتنسيق المحتوى (04 §11.3)، وربط الكشيدة.
     التفضيلات في localStorage («maktaba:reader»): size 1–5 · spacing · align · rtheme. بدون أي بيانات شخصية، وتنطبق على كل الموضوعات.
     ========================================================================== */
  /* تنسيق محتوى المحرر (04 §11.3) — عام للموضوع والصفحات: الجداول، والصور مع التعليق، والتنبيهات «خلاصة: / تنبيه: / نصيحة:».
     الإعدادات من سمات العنصر: data-captions="true"، data-callouts="خلاصة|تنبيه|نصيحة" */
  function enhanceRte(body, host = body) {
    if (!body || body.dataset.rteDone) return;
    body.dataset.rteDone = '1';
      body.querySelectorAll('table').forEach((t) => {
        if (t.parentElement.classList.contains('rte-table')) return;
        const w = document.createElement('div');
        w.className = 'rte-table';
        w.tabIndex = 0;
        t.replaceWith(w);
        w.appendChild(t);
      });
      if (host.dataset.captions === 'true') {
        body.querySelectorAll('img[alt]').forEach((img) => {
          const alt = img.getAttribute('alt').trim();
          if (!alt || img.closest('figure')) return;
          const fig = document.createElement('figure');
          fig.className = 'rte-figure';
          const host = img.parentElement && img.parentElement.tagName === 'P' && img.parentElement.textContent.trim() === '' && img.parentElement.children.length === 1 ? img.parentElement : img;
          host.replaceWith(fig);
          fig.appendChild(img);
          const cap = document.createElement('figcaption');
          cap.textContent = alt;
          fig.appendChild(cap);
        });
      }
      const words = (host.dataset.callouts || '').split('|');
      if (words.length === 3) {
        body.querySelectorAll(':scope > p').forEach((p) => {
          const text = p.textContent.trim();
          const k = words.findIndex((w) => w && new RegExp(`^${escRe(w)}\\s*[:：]`).test(text));
          if (k < 0) return;
          const kind = CALLOUT_KINDS[k];
          p.innerHTML = p.innerHTML.replace(new RegExp(`^\\s*(<(strong|b)>\\s*)?${escRe(words[k])}\\s*(</(strong|b)>\\s*)?[:：]\\s*(</(strong|b)>\\s*)?`), '');
          const box = document.createElement('div');
          box.className = `rcallout rcallout--${kind}`;
          box.setAttribute('role', 'note');
          const title = document.createElement('p');
          title.className = 'rcallout__title';
          title.innerHTML = `<svg class="icon icon--sm" aria-hidden="true" focusable="false"><use href="#icon-${CALLOUT_ICONS[kind]}"></use></svg>`;
          title.appendChild(document.createTextNode(words[k]));
          p.replaceWith(box);
          box.appendChild(title);
          box.appendChild(p);
        });
      }
      }
  const READER_KEY = 'maktaba:reader';
  const R_VALUES = { size: ['1', '2', '3', '4', '5'], spacing: ['narrow', 'comfortable', 'wide'], align: ['start', 'justify'], rtheme: ['default', 'paper', 'night'] };
  const CALLOUT_KINDS = ['summary', 'warning', 'tip'];
  const CALLOUT_ICONS = { summary: 'check', warning: 'alert', tip: 'info' };
  const escRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  class TopicReader extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.body = this.querySelector('[data-article-body]');
      this.tools = this.querySelector('[data-rtools]');
      this.compact = this.querySelector('[data-rtools-compact]');
      this.bar = this.querySelector('[data-rprogress]');
      if (!this.body) return;
      this.enhance();
      this.buildToc();
      if (this.tools) this.tools.hidden = false;
      this.addEventListener('click', (e) => {
        const b = e.target.closest('[data-r]');
        if (b && this.contains(b)) this.onTool(b);
      });
      this.sync();
      document.documentElement.classList.add('has-reader');
      this.onScroll = () => {
        if (this.raf) return;
        this.raf = requestAnimationFrame(() => { this.raf = null; this.update(); });
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });
      this.update();
      if (window.Kashida) this.applyAlign();
      else document.addEventListener('kashida:ready', () => this.applyAlign(), { once: true });
    }
    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
      if (this.tocIo) this.tocIo.disconnect();
    }
    defaults() {
      return { size: this.dataset.defaultSize, spacing: this.dataset.defaultSpacing, align: this.dataset.defaultAlign, rtheme: this.dataset.defaultRtheme };
    }
    get(key) {
      const v = this.dataset[key];
      return R_VALUES[key].includes(v) ? v : this.defaults()[key];
    }
    set(key, val, save = true) {
      if (!R_VALUES[key] || !R_VALUES[key].includes(val) || this.dataset[key] === val) return;
      this.dataset[key] = val;
      if (save) {
        const p = utils.storage.get(READER_KEY) || {};
        p[key] = val;
        utils.storage.set(READER_KEY, p);
      }
      this.sync();
      if (key === 'align') this.applyAlign();
      if (key === 'size' || key === 'spacing') document.dispatchEvent(new CustomEvent('reader:scale'));
      this.update();
    }
    onTool(b) {
      const key = b.dataset.r;
      const v = b.dataset.v;
      if (key === 'reset') {
        utils.storage.remove(READER_KEY);
        const d = this.defaults();
        Object.keys(d).forEach((k) => this.set(k, d[k], false));
        const more = b.closest('details');
        if (more) { more.open = false; more.querySelector('summary').focus(); }
        return;
      }
      if (key === 'size') {
        const cur = Number(this.get('size'));
        let next = cur;
        if (v === '+1') next = Math.min(5, cur + 1);
        else if (v === '-1') next = Math.max(1, cur - 1);
        else next = Number(v.slice(1));
        this.set('size', String(next));
        return;
      }
      this.set(key, v);
    }
    sync() {
      const size = Number(this.get('size'));
      this.querySelectorAll('[data-r]').forEach((b) => {
        const key = b.dataset.r;
        const v = b.dataset.v;
        if (key === 'size') {
          if (v === '+1' || v === '-1') {
            const atEdge = v === '+1' ? size >= 5 : size <= 1;
            b.disabled = atEdge;
          } else {
            const lvl = Number(v.slice(1));
            b.setAttribute('aria-pressed', String(lvl === size));
            b.classList.toggle('is-filled', lvl <= size);
          }
        } else if (R_VALUES[key]) {
          b.setAttribute('aria-pressed', String(this.get(key) === v));
        }
      });
    }
    applyAlign() {
      if (!window.Kashida) return;
      if (this.get('align') === 'justify') window.Kashida.apply(this.body);
      else window.Kashida.clear(this.body);
    }
    enhance() { enhanceRte(this.body, this); }
    /* الفهرس من H2/H3 — يختفي إذا أقل من عنوانين */
    buildToc() {
      const nav = this.querySelector('[data-rtoc]');
      if (!nav || this.dataset.toc !== 'true') return;
      const hs = [...this.body.querySelectorAll('h2, h3')].filter((h) => h.textContent.trim());
      if (hs.length < 2) { nav.remove(); return; }
      const list = nav.querySelector('[data-rtoc-list]');
      const used = new Set();
      hs.forEach((h, i) => {
        let id = h.id;
        if (!id || used.has(id) || document.querySelectorAll(`[id="${CSS.escape(id)}"]`).length > 1) id = `toc-${i + 1}`;
        h.id = id;
        used.add(id);
        const li = document.createElement('li');
        li.className = `rtoc__item rtoc__item--${h.tagName.toLowerCase()}`;
        const a = document.createElement('a');
        a.href = `#${id}`;
        a.textContent = h.textContent.trim();
        li.appendChild(a);
        list.appendChild(li);
      });
      const count = nav.querySelector('[data-rtoc-count]');
      if (count) count.textContent = `(${utils.formatNumber ? utils.formatNumber(hs.length) : hs.length})`;
      nav.hidden = false;
      this.classList.add('has-toc');
      const details = nav.querySelector('[data-rtoc-details]');
      const side = () => {
        const w = window.innerWidth;
        if (w >= 1200) return this.classList.contains('topic--toc-d-side');
        if (w >= 750) return this.classList.contains('topic--toc-t-side');
        return false;
      };
      const applyMode = () => {
        const isSide = side();
        this.classList.toggle('is-toc-side', isSide);
        if (isSide) details.open = true;
        else if (!this.tocTouched) details.open = false;
      };
      applyMode();
      window.addEventListener('resize', utils.debounce(applyMode, 150));
      details.querySelector('summary').addEventListener('click', (e) => {
        if (this.classList.contains('is-toc-side')) { e.preventDefault(); return; }
        this.tocTouched = true;
      });
      list.addEventListener('click', (e) => {
        const a = e.target.closest('a');
        if (!a) return;
        const target = document.getElementById(decodeURIComponent(a.hash.slice(1)));
        if (!target) return;
        e.preventDefault();
        // نقفل الفهرس (الجوال) قبل الحساب، لأن قفله يغيّر مكان العنوان
        if (!this.classList.contains('is-toc-side')) details.open = false;
        target.scrollIntoView({ behavior: utils.reducedMotion() ? 'auto' : 'smooth', block: 'start' });
        target.setAttribute('tabindex', '-1');
        target.focus({ preventScroll: true });
        window.history.replaceState(window.history.state, '', a.hash);
      });
      if ('IntersectionObserver' in window) {
        const links = new Map([...list.querySelectorAll('a')].map((a) => [a.hash.slice(1), a]));
        this.tocIo = new IntersectionObserver((entries) => {
          entries.forEach((en) => {
            if (!en.isIntersecting) return;
            links.forEach((a) => a.removeAttribute('aria-current'));
            const a = links.get(en.target.id);
            if (a) a.setAttribute('aria-current', 'location');
          });
        }, { rootMargin: '-15% 0px -70% 0px' });
        hs.forEach((h) => this.tocIo.observe(h));
      }
    }
    /* شريط التقدم + الشريط المضغوط الثابت */
    update() {
      const r = this.body.getBoundingClientRect();
      const vh = window.innerHeight;
      if (this.bar) {
        let p = (vh * 0.3 - r.top) / Math.max(1, r.height - vh * 0.4);
        const doc = document.documentElement;
        if (window.scrollY + vh >= doc.scrollHeight - 2 && r.bottom < vh) p = 1;
        p = Math.min(1, Math.max(0, p));
        this.bar.style.setProperty('--p', p.toFixed(4));
        this.bar.classList.toggle('is-active', r.top < vh * 0.3 && r.bottom > 0);
      }
      if (this.compact && this.tools && this.dataset.sticky === 'true') {
        const t = this.tools.getBoundingClientRect();
        const show = t.bottom < 0 && r.bottom > 120;
        if (show !== !this.compact.hidden) {
          if (show) this.compact.hidden = false;
          this.compact.classList.toggle('is-visible', show);
          if (!show) {
            if (this.compact.contains(document.activeElement)) this.tools.querySelector('button')?.focus({ preventScroll: true });
            this.compact.hidden = true;
          }
        }
      }
    }
  }
  theme.define('topic-reader', TopicReader);

  /* ==========================================================================
     المرحلة 8: النماذج (تحقق عربي تحت الحقول + إظهار كلمة المرور)، وتبويبات الحساب، ولوحتا الدخول/الاستعادة، وبحث الأسئلة.
     ========================================================================== */
  const fieldError = (input, msg) => {
    const field = input.closest('[data-field]');
    const box = field && field.querySelector('[data-field-error]');
    if (!box) return;
    const ids = (input.getAttribute('aria-describedby') || '').split(' ').filter((x) => x && x !== box.id);
    if (msg) {
      box.querySelector('span').textContent = msg;
      box.hidden = false;
      field.classList.add('field--error');
      input.setAttribute('aria-invalid', 'true');
      ids.push(box.id);
    } else {
      box.hidden = true;
      field.classList.remove('field--error');
      input.removeAttribute('aria-invalid');
    }
    if (ids.length) input.setAttribute('aria-describedby', ids.join(' ')); else input.removeAttribute('aria-describedby');
  };
  const checkField = (input) => {
    const v = input.value.trim();
    if (input.required && !v) return input.dataset.errorRequired || '';
    if (v && input.type === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v)) return input.dataset.errorInvalid || '';
    if (v && input.minLength > 0 && v.length < input.minLength) return input.dataset.errorShort || '';
    if (input.dataset.match) {
      const other = document.getElementById(input.dataset.match);
      if (other && other.value !== input.value) return input.dataset.errorMatch || '';
    }
    return '';
  };
  function initForms(scope = document) {
    scope.querySelectorAll('form.aform').forEach((form) => {
      if (form.dataset.vInit) return;
      form.dataset.vInit = '1';
      form.noValidate = true;
      form.addEventListener('submit', (e) => {
        if (e.submitter && e.submitter.hasAttribute('formnovalidate')) return;
        let first = null;
        form.querySelectorAll('[data-field] input, [data-field] textarea').forEach((input) => {
          const msg = checkField(input);
          fieldError(input, msg);
          if (msg && !first) first = input;
        });
        if (first) { e.preventDefault(); first.focus(); }
      });
      form.addEventListener('input', (e) => {
        const input = e.target;
        if (input.getAttribute('aria-invalid') === 'true') fieldError(input, checkField(input));
      });
    });
    scope.querySelectorAll('[data-pw-toggle]').forEach((b) => {
      if (b.dataset.init) return;
      b.dataset.init = '1';
      b.addEventListener('click', () => {
        const input = document.getElementById(b.getAttribute('aria-controls'));
        const show = input.type === 'password';
        input.type = show ? 'text' : 'password';
        b.setAttribute('aria-pressed', String(show));
        b.setAttribute('aria-label', show ? b.dataset.hide : b.dataset.show);
      });
    });
    scope.querySelectorAll('.falert[tabindex="-1"]').forEach((a) => { if (!a.dataset.f) { a.dataset.f = '1'; a.focus({ preventScroll: false }); } });
    scope.querySelectorAll('select[data-default]').forEach((s) => { if (s.dataset.default) s.value = s.dataset.default; });
  }

  /* الدخول ↔ نسيت كلمة المرور (نفس الصفحة). بدون JS: :target */
  class AuthPanels extends HTMLElement {
    connectedCallback() {
      this.show(window.location.hash === '#recover' ? 'recover' : this.dataset.open || 'login', false);
      this.addEventListener('click', (e) => {
        const a = e.target.closest('[data-show-panel]');
        if (!a) return;
        e.preventDefault();
        this.show(a.dataset.showPanel, true);
        window.history.replaceState(window.history.state, '', `#${a.dataset.showPanel}`);
      });
    }
    show(name, focus) {
      this.querySelectorAll('[data-panel]').forEach((p) => {
        p.hidden = p.dataset.panel !== name;
        p.removeAttribute('data-target-only');
      });
      if (focus) {
        const p = this.querySelector(`[data-panel="${name}"]`);
        const t = p && p.querySelector('h1, h2');
        if (t) { t.setAttribute('tabindex', '-1'); t.focus(); }
      }
    }
  }
  theme.define('auth-panels', AuthPanels);

  /* تبويبات لوحة الحساب: #home / #orders / #details / #help — بدون JS كلها تحت بعض */
  class AccountTabs extends HTMLElement {
    connectedCallback() {
      this.classList.add('is-tabs');
      const fromHash = () => (window.location.hash || '').slice(1);
      const initial = this.querySelector(`[data-tab-panel="${fromHash()}"]`) ? fromHash() : (Number(this.dataset.page) > 1 ? 'orders' : 'home');
      this.show(initial, false);
      this.addEventListener('click', (e) => {
        const a = e.target.closest('[data-account-tab]');
        if (!a) return;
        e.preventDefault();
        this.show(a.dataset.accountTab, true);
        window.history.replaceState(window.history.state, '', `#${a.dataset.accountTab}`);
      });
      window.addEventListener('hashchange', () => { if (this.querySelector(`[data-tab-panel="${fromHash()}"]`)) this.show(fromHash(), false); });
    }
    show(name, focus) {
      this.querySelectorAll('[data-tab-panel]').forEach((p) => { p.hidden = p.dataset.tabPanel !== name; });
      this.querySelectorAll('.anav [data-account-tab]').forEach((a) => {
        if (a.dataset.accountTab === name) a.setAttribute('aria-current', 'page'); else a.removeAttribute('aria-current');
      });
      if (focus) {
        const h = this.querySelector(`[data-tab-panel="${name}"] h1, [data-tab-panel="${name}"] h2`);
        if (h) { h.setAttribute('tabindex', '-1'); h.focus({ preventScroll: true }); }
        const top = this.getBoundingClientRect().top + window.scrollY - 90;
        if (window.scrollY > top) window.scrollTo({ top, behavior: utils.reducedMotion() ? 'auto' : 'smooth' });
      }
    }
  }
  theme.define('account-tabs', AccountTabs);

  /* بحث فوري في الأسئلة (كل أقسام الأسئلة في الصفحة) — تطبيع عربي */
  class FaqSearch extends HTMLElement {
    connectedCallback() {
      this.hidden = false;
      this.input = this.querySelector('[data-faq-input]');
      this.status = this.querySelector('[data-faq-status]');
      this.empty = this.querySelector('[data-faq-empty]');
      this.input.addEventListener('input', utils.debounce(() => this.filter(), 120));
    }
    plural(n) {
      const d = this.dataset;
      const k = n === 1 ? 'resultsOne' : n === 2 ? 'resultsTwo' : (n % 100 >= 3 && n % 100 <= 10) ? 'resultsFew' : (n % 100 >= 11) ? 'resultsMany' : 'resultsOther';
      return (d[k] || d.resultsOther || '').replace('__COUNT__', n);
    }
    filter() {
      const raw = this.input.value.trim();
      const q = utils.foldArabic(raw);
      const items = document.querySelectorAll('[data-faq-root] details.c-faq');
      let shown = 0;
      items.forEach((d) => {
        const hay = utils.foldArabic(d.textContent);
        const ok = !q || hay.includes(q);
        d.hidden = !ok;
        if (ok) shown += 1;
        if (q && ok && !d.dataset.wasOpen) { d.dataset.wasOpen = d.open ? '1' : '0'; }
        if (q && ok && hay.indexOf(q) > (d.querySelector('summary').textContent.length)) d.open = true;
        if (!q && d.dataset.wasOpen) { d.open = d.dataset.wasOpen === '1'; delete d.dataset.wasOpen; }
      });
      document.querySelectorAll('[data-faq-root] [data-faq-group]').forEach((g) => {
        g.hidden = !!q && !g.querySelector('details.c-faq:not([hidden])');
      });
      this.empty.hidden = !q || shown > 0;
      if (q && !shown) this.querySelector('[data-faq-empty-title]').textContent = this.dataset.noMatch.replace('__TERMS__', raw);
      this.status.textContent = q && shown ? this.plural(shown) : '';
    }
  }
  theme.define('faq-search', FaqSearch);

  /* بلوك «أسئلة»: «أول سؤال مفتوح» و«افتح سؤالًا واحدًا فقط» (name على details) */
  function initFaqBlocks(scope = document) {
    scope.querySelectorAll('.cb-faq').forEach((box) => {
      const items = box.querySelectorAll('details.c-faq');
      if (box.classList.contains('cb-faq--single')) items.forEach((d) => d.setAttribute('name', box.dataset.faqGroup));
      if (box.classList.contains('cb-faq--first-open') && items[0] && !box.dataset.faqInit) items[0].open = true;
      box.dataset.faqInit = '1';
    });
  }

  function init(scope = document) {
    observeMeasures(scope);
    initFooter(scope);
    initCheckers(scope);
    initFaqBlocks(scope);
    scope.querySelectorAll('[data-rte-enhance]').forEach((el) => enhanceRte(el));
    initForms(scope);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  /* محرر الثيم: إعادة التهيئة عند إضافة/تعديل قسم */
  document.addEventListener('shopify:section:load', (e) => init(e.target));
  /* اختيار شريحة في المحرر: نروح لها ونوقف التشغيل التلقائي */
  document.addEventListener('shopify:block:select', (e) => {
    const slider = e.target.closest('hero-slider');
    if (!slider || !slider.go) return;
    slider.stopForGood();
    slider.go(Number(e.target.dataset.index) || 0);
  });
  document.addEventListener('shopify:section:select', (e) => {
    const drawer = e.target.querySelector('menu-drawer');
    if (drawer && drawer.open) drawer.open();
  });
  document.addEventListener('shopify:section:deselect', (e) => {
    const drawer = e.target.querySelector('menu-drawer');
    if (drawer && drawer.close) drawer.close();
  });

  window.addEventListener('offline', () => theme.toast(S().offline, { type: 'error' }));
})();
